import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { mapPedidoFromApi } from "../lib/pedidoMapper";
import { medidaDoItem, qtdDoItem } from "../lib/unidadeVenda";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  Search, Grid3X3, List, Clock, Truck, CheckCircle, XCircle,
  Package, User, Phone, MapPin, CreditCard, QrCode,
  Calendar as CalendarIcon, X, ChevronRight, ShoppingBag, UtensilsCrossed, MessageCircle, Bike, RefreshCw,
  DollarSign, Receipt, TrendingUp, Download, Filter, ChevronDown, ChevronUp, Printer, FileText, ChefHat, AlertTriangle, MessageSquare, Check, Layers, Ticket, Sparkles, Star,
  Megaphone,
  ClipboardList,
} from "lucide-react";
import { CaixaRegistradoraIcon, MesaIcon, MotoEntregaIcon, TotemIcon } from "@/components/icons/ModulosIcons";
import { SeloNotaFiscal, reimprimirComNota, NotasFiscaisProvider } from "../features/fiscal";
import { emojiPagamento, rotuloPagamento } from "../features/pedidos/lib/pagamentoRotulo";
import { partesDoPedido, juntarPartes, casaFiltroPagamento, textoPartes } from "../features/pedidos/lib/pagamentoPartes";
import { useOpcoesPagamento } from "../features/pedidos/lib/useOpcoesPagamento";
import FormasPagamentoPedido from "../features/pedidos/components/FormasPagamentoPedido";
import DetalhePagamentoExtra from "../features/pedidos/components/DetalhePagamentoExtra";
import { seloTipoEntrega } from "../features/pedidos/lib/tipoEntregaSelo";
import { CarregandoGrade, CarregandoTabela } from "../components/carregando";
import { Skeleton } from "../components/ui/skeleton";
import { PERMISSAO_CANCELAR } from "../features/funcionarios";
import { CancelReasonSelect } from "../features/ifood";
import { HistoricoReabertura, ReabrirPedido, SeloReaberto, useReabertura } from "../features/pedidos-reabrir";
import { SeloCleo } from "../features/cleo-pedidos";
import { imprimirCupom } from "../utils/cupomDelivery";
import { getToken } from "../utils/tokenStorage";
import { temAcessoTotal, temPermissao, usuarioLogado } from "../lib/permissoes";
import { imprimirCupomFechamento } from "../utils/cupomFechamento";
import { imprimirCupomPreparo } from "../utils/cupomPreparo";
import { hasAccess, PlanBadge, getPlanLabel } from "../components/PlanGate";

// Ícone do WhatsApp customizado (mesmo do Delivery)
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Logo Nucleo como ícone (mesmo do Delivery)
const NucleoLogoIcon = ({ className }) => (
  <img src="/logo-nucleo.png" alt="Nucleo" className={className} style={{ objectFit: 'contain' }} />
);

// Logo iFood (imagem real)
const IFoodIcon = ({ className }) => (
  <img src="/logo-ifood.png" alt="iFood" className={className} style={{ objectFit: 'contain' }} />
);

// Logo Grandchef (imagem real)
const GrandchefIcon = ({ className }) => (
  <img src="/logo-grandchef.png" alt="Grandchef" className={className} style={{ objectFit: 'contain' }} />
);

// Logo AnotaAi (imagem real)
const AnotaAiIcon = ({ className }) => (
  <img src="/logo-anotaai.png" alt="AnotaAi" className={className} style={{ objectFit: 'contain' }} />
);

// Módulos considerados "integração externa" (pedidos importados de outros sistemas).
// A logo apresentada para o cliente vem do setting `integration_source` do tenant.
const INTEGRATION_MODULES = ['Grandchef', 'AnotaAi'];
// Opção do filtro Módulo que não é módulo: pedidos anotados pela CLEO na conversa.
// O servidor entende a mesma chave no /pedidos/stats (database._where_pedidos_filtrados).
const FILTRO_CLEO = 'CLEO';

// Componente de logo da integração: usa a logo definida pelo tenant (default: Grandchef).
const IntegrationIcon = ({ className, source }) => {
  if (source === 'anotaai') return <AnotaAiIcon className={className} />;
  return <GrandchefIcon className={className} />;
};

// Opções das caixas de seleção de filtro. Ficam fora do componente pra não
// serem recriadas a cada render (e pra ficarem visíveis num lugar só).
// Largura da coluna do resumo, usada pelo cabeçalho e pelo painel.
const LARGURA_PAINEL = 330;

const OPCOES_STATUS = [
  { value: 'agendado', label: 'Agendado' },
  { value: 'aguardando_aceite', label: 'Aguardando aceite' },
  { value: 'producao', label: 'Em produção' },
  { value: 'pronto', label: 'Pronto' },
  { value: 'na_bag', label: 'Na bag' },
  { value: 'em_rota', label: 'Em rota' },
  { value: 'concluido', label: 'Concluído' },
  { value: 'cancelado', label: 'Cancelado' },
];

// Opções de Pagamento vêm do servidor (useOpcoesPagamento): uma por grupo de
// forma, com a mesma chave que o resumo filtra. "Múltiplo" não existe mais.

// Canais de venda mostrados no painel de resumo, na ordem em que aparecem.
// Mesa QR entra separado de Mesas de propósito: é o mesmo salão, mas quem
// montou o pedido foi o cliente, e o lojista quer enxergar isso.
const MODULOS_RESUMO = [
  { key: 'Mesas', label: 'Mesas', Icon: MesaIcon, isImg: false,
    iconColor: 'text-blue-500', barColor: 'bg-blue-500', requiredPlan: 'start' },
  { key: 'MesaQR', label: 'Mesa QR', Icon: QrCode, isImg: false,
    iconColor: 'text-cyan-500', barColor: 'bg-cyan-500', requiredPlan: 'start' },
  { key: 'Comandas', label: 'Comandas', Icon: ClipboardList, isImg: false,
    iconColor: 'text-amber-500', barColor: 'bg-amber-500', requiredPlan: 'start' },
  { key: 'Delivery', label: 'Delivery', Icon: MotoEntregaIcon, isImg: false,
    iconColor: 'text-orange-500', barColor: 'bg-orange-500', requiredPlan: 'start' },
  { key: 'Balcao', label: 'Balcão', Icon: CaixaRegistradoraIcon, isImg: false,
    iconColor: 'text-purple-500', barColor: 'bg-purple-500', requiredPlan: 'completo' },
  { key: 'Totem', label: 'Totem', Icon: TotemIcon, isImg: false,
    iconColor: 'text-violet-500', barColor: 'bg-violet-500', requiredPlan: 'completo' },
  { key: 'Cardapio', label: 'Cardápio', Icon: NucleoLogoIcon, isImg: true,
    iconColor: '', barColor: 'bg-pink-500', requiredPlan: 'start' },
  { key: 'WhatsApp', label: 'WhatsApp', Icon: WhatsAppIcon, isImg: false,
    iconColor: 'text-green-500', barColor: 'bg-green-500', requiredPlan: 'start' },
  { key: 'iFood', label: 'iFood', Icon: IFoodIcon, isImg: true,
    iconColor: '', barColor: 'bg-red-500', requiredPlan: 'start' },
];
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import TablePagination from "../components/TablePagination";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { ptBR } from "date-fns/locale/pt-BR";
import { useNavigation } from "react-day-picker";
import { ChevronLeft } from "lucide-react";
import PainelResumo from "../features/pedidos-resumo/PainelResumo";
import CabecalhoPedidos from "../features/pedidos-resumo/CabecalhoPedidos";
import { montarGrupos, calcularAutomacao, MODULOS_IMPORTACAO } from "../features/pedidos-resumo/grupos";
import ResumoFaixa from "../features/pedidos-resumo/ResumoFaixa";
import SeloOfertaItem from "../features/ofertas-do-dia/admin/SeloOfertaItem";
import { descontoExclusivo, precoCheioOuPago } from "../lib/descontoExclusivo";
import ChipsAtivos from "../features/pedidos-resumo/ChipsAtivos";
import FiltroMulti from "../features/pedidos-filtros/FiltroMulti";
import SeloOrigem, { OPCOES_ORIGEM } from "../features/pedidos-filtros/SeloOrigem";
import FiltroPeriodo from "../features/pedidos-filtros/FiltroPeriodo";
import { useResumoPedidos } from "../features/pedidos-resumo/useResumoPedidos";

// Cabeçalho do calendário — substitui o `caption` padrão do react-day-picker.
// O `captionLayout="dropdown-buttons"` da lib mistura dropdown nativo HTML
// sem estilo + label duplicado, resultado horrível. Aqui usamos Select shadcn
// e mantemos as setas. As props `minDate`/`maxDate` limitam o que aparece
// nos dropdowns — nada de meses/anos sem pedidos (passado) ou no futuro.
const MONTHS_FULL_PT = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
function makeCalendarCaption(minDate, maxDate) {
  return function CalendarMonthYearCaption({ displayMonth }) {
    const { goToMonth, previousMonth, nextMonth } = useNavigation();
    const minY = minDate.getFullYear();
    const maxY = maxDate.getFullYear();
    const years = [];
    for (let y = minY; y <= maxY; y++) years.push(y);
    const year = displayMonth.getFullYear();
    const monthsInYear = MONTHS_FULL_PT.map((label, idx) => ({ label, idx })).filter(({ idx }) => {
      const first = new Date(year, idx, 1);
      const last = new Date(year, idx + 1, 0);
      return last >= new Date(minDate.getFullYear(), minDate.getMonth(), 1)
        && first <= new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
    });
    const handleMonthChange = (v) => goToMonth(new Date(year, Number(v), 1));
    const handleYearChange = (v) => {
      const newYear = Number(v);
      let m = displayMonth.getMonth();
      const firstAllowed = newYear === minY ? minDate.getMonth() : 0;
      const lastAllowed = newYear === maxY ? maxDate.getMonth() : 11;
      m = Math.max(firstAllowed, Math.min(lastAllowed, m));
      goToMonth(new Date(newYear, m, 1));
    };
    return (
      <div className="flex items-center justify-between gap-1 py-0.5 px-1">
        <button
          type="button"
          onClick={() => previousMonth && goToMonth(previousMonth)}
          disabled={!previousMonth}
          className="h-7 w-7 rounded-md border border-input flex items-center justify-center hover:bg-accent disabled:opacity-0 disabled:pointer-events-none"
          aria-label="Mês anterior"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-1.5 flex-1 justify-center">
          <Select value={String(displayMonth.getMonth())} onValueChange={handleMonthChange}>
            <SelectTrigger className="h-7 text-xs px-2 w-[112px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {monthsInYear.map(({ label, idx }) => (
                <SelectItem key={idx} value={String(idx)} className="text-xs">{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={handleYearChange}>
            <SelectTrigger className="h-7 text-xs px-2 w-[80px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {years.map((y) => (<SelectItem key={y} value={String(y)} className="text-xs">{y}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <button
          type="button"
          onClick={() => nextMonth && goToMonth(nextMonth)}
          disabled={!nextMonth}
          className="h-7 w-7 rounded-md border border-input flex items-center justify-center hover:bg-accent disabled:opacity-0 disabled:pointer-events-none"
          aria-label="Próximo mês"
        >
          <ChevronLeft className="h-4 w-4 rotate-180" />
        </button>
      </div>
    );
  };
}

const API = '/api';

// Formatadores BR: dinheiro (R$ 1.234.567,89) e número (1.234.567)
const fmtMoney = (v) => 'R$ ' + (Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtNum = (v) => (Number(v) || 0).toLocaleString('pt-BR');

// Buscar foto no productsMap com fallback parcial (contains)
const findProductPhoto = (name, productsMap) => {
  if (!name || !productsMap) return null;
  const lower = name.toLowerCase().trim();
  // Exact match
  if (productsMap[lower]) return productsMap[lower];
  // Partial: product name contains subitem name or vice-versa
  for (const [key, url] of Object.entries(productsMap)) {
    if (key.startsWith('id_')) continue;
    if (key.includes(lower) || lower.includes(key)) return url;
  }
  return null;
};

// Converte URL de foto (relativa ou /uploads) para URL absoluta via /api
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/uploads/")) return `/api${url}`;
  if (url.startsWith("uploads/")) return `/api/${url}`;
  return url.startsWith("/") ? `/api${url}` : url;
};

// Status dos pedidos com suas cores e ícones
const statusConfig = {
  agendado: {
    label: "Agendado",
    color: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400",
    icon: CalendarIcon,
  },
  aguardando_aceite: {
    label: "Aguardando Aceite",
    color: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
    icon: Clock
  },
  producao: { 
    label: "Em Produção", 
    color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
    icon: Clock 
  },
  pronto: { 
    label: "Pronto", 
    color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
    icon: Package 
  },
  na_bag: { 
    label: "Na Bag", 
    color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
    icon: Package 
  },
  em_rota: { 
    label: "Em Rota", 
    color: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
    icon: Truck 
  },
  transito: { 
    label: "Em Trânsito", 
    color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
    icon: Truck 
  },
  concluido: { 
    label: "Concluído", 
    color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
    icon: CheckCircle 
  },
  cancelado: { 
    label: "Cancelado", 
    color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
    icon: XCircle 
  },
  // Mesa reaberta (Vendas → Pedidos) aguardando o novo fechamento
  entregue: {
    label: "Aguardando pagamento",
    color: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
    icon: Clock
  },
  // Status legado do Delivery
  aguardando: { 
    label: "Em Produção", 
    color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
    icon: Clock 
  },
};

// Formatar telefone com máscara (99)9.9999-9999 (mesmo do Delivery)
function formatPhoneNumber(phone) {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 13) {
    // 55 + DDD + 9 dígitos: (XX)X.XXXX-XXXX
    return `(${digits.slice(2,4)})${digits[4]}.${digits.slice(5,9)}-${digits.slice(9)}`;
  } else if (digits.length === 12) {
    // 55 + DDD + 8 dígitos: (XX)XXXX-XXXX
    return `(${digits.slice(2,4)})${digits.slice(4,8)}-${digits.slice(8)}`;
  } else if (digits.length === 11) {
    return `(${digits.slice(0,2)})${digits[2]}.${digits.slice(3,7)}-${digits.slice(7)}`;
  } else if (digits.length === 10) {
    return `(${digits.slice(0,2)})${digits.slice(2,6)}-${digits.slice(6)}`;
  }
  return phone;
}

// Módulos de origem (ícones iguais ao Delivery)
const modulosConfig = {
  Delivery: { label: "Delivery", icon: MotoEntregaIcon, color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" },
  Balcao: { label: "Balcão", icon: CaixaRegistradoraIcon, color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" },
  Totem: { label: "Totem", icon: TotemIcon, color: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400" },
  Mesas: { label: "Mesas", icon: MesaIcon, color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  // Sem esta linha o pedido da Mesa QR ficava sem selo de módulo no card e no detalhe.
  MesaQR: { label: "Mesa QR", icon: QrCode, color: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400" },
  Comandas: { label: "Comandas", icon: ClipboardList, color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  ChatBot: { label: "ChatBot", icon: MessageCircle, color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  WhatsApp: { label: "WhatsApp", icon: WhatsAppIcon, color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  Cardapio: { label: "Cardápio Digital", icon: NucleoLogoIcon, color: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400" },
  CardapioPublico: { label: "Cardápio Digital", icon: NucleoLogoIcon, color: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400" },
  iFood: { label: "iFood", icon: IFoodIcon, color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
  Grandchef: { label: "Integração", icon: GrandchefIcon, color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  AnotaAi: { label: "Integração", icon: AnotaAiIcon, color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
};

// Lookup case-insensitive para módulo
function getModulo(modulo) {
  if (!modulo) return null;
  if (modulosConfig[modulo]) return modulosConfig[modulo];
  const lower = modulo.toLowerCase();
  for (const [key, val] of Object.entries(modulosConfig)) {
    if (key.toLowerCase() === lower) return val;
  }
  return null;
}

function PedidosInterno({ userPlan = "enterprise" }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const filterCaixaId = searchParams.get("caixa_id") || "";
  const filterClienteParam = searchParams.get("cliente") || "";

  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(filterClienteParam);
  // Debounce do searchTerm — evita refetch a cada keystroke. Quando o termo
  // estabiliza por 350ms, o useEffect do fetchPedidos dispara.
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(filterClienteParam);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);
  const [viewMode, setViewMode] = useState("grade"); // "grade" ou "lista"
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Reabrir pedido concluído: o servidor diz se pode (caixa aberto, permissão)
  const { info: reabertura } = useReabertura(selectedPedido, detailsOpen);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  // Pedido iFood: motivo escolhido da lista do iFood (obrigatório na homologação)
  const [ifoodCancelReason, setIfoodCancelReason] = useState(null);
  const isIfoodSelected = (selectedPedido?.modulo || "").toLowerCase() === "ifood";
  const [cancelError, setCancelError] = useState("");
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Stats globais agregadas no servidor (independente da lista carregada)
  const [serverStats, setServerStats] = useState(null);

  // Estados de filtros avançados
  const [showFilters, setShowFilters] = useState(false);
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  // Hora opcional pra refinar o período (ex: "10:00" → "02:00"). Vazio = dia inteiro.
  const [filterTimeFrom, setFilterTimeFrom] = useState("");
  const [filterTimeTo, setFilterTimeTo] = useState("");
  // Estado RASCUNHO do popover de período: o filtro só é aplicado quando o
  // usuário clica em Aplicar. Antes era aplicado a cada clique no calendário
  // e disparava refetch imediato — reclamação do dono.
  const [dateFilterOpen, setDateFilterOpen] = useState(false);
  const [pendingDateFrom, setPendingDateFrom] = useState("");
  const [pendingDateTo, setPendingDateTo] = useState("");
  const [pendingTimeFrom, setPendingTimeFrom] = useState("");
  const [pendingTimeTo, setPendingTimeTo] = useState("");
  // Controla se o bloco "Personalizado" (datetime-local) está aberto.
  const [customDateOpen, setCustomDateOpen] = useState(false);
  const [filterModules, setFilterModules] = useState([]);
  // Compat: filterModule derived from filterModules
  const filterModule = filterModules.length === 1 ? filterModules[0] : filterModules.length === 0 ? "todos" : "multi";
  const setFilterModule = (v) => { if (v === "todos") setFilterModules([]); else setFilterModules([v]); };
  const [filterPayments, setFilterPayments] = useState([]);
  // Variação dentro do canal (entrega x retirada) — o que separa o pedido
  // que o atendente digitou do que o cliente fechou sozinho.
  const [filterTiposEntrega, setFilterTiposEntrega] = useState([]);
  const [filterCaixa, setFilterCaixa] = useState(filterCaixaId || "todos");
  const [caixasList, setCaixasList] = useState([]);
  const [filterEntregadores, setFilterEntregadores] = useState([]);
  const [productsMap, setProductsMap] = useState({});
  // Quilo/litro (30/09/2026): o item do pedido não guarda a unidade de venda;
  // o cadastro diz se o produto é por peso ("1,250 kg" no detalhe).
  const [produtosCadastro, setProdutosCadastro] = useState([]);
  const [integrationSource, setIntegrationSource] = useState('grandchef');

  useEffect(() => {
    const fetchIntegrationSource = async () => {
      try {
        const tok = localStorage.getItem("token");
        const res = await fetch(`${API}/company/settings`, {
          headers: tok ? { Authorization: `Bearer ${tok}` } : {}
        });
        if (!res.ok) return;
        const data = await res.json();
        const src = (data?.integration_source || '').toLowerCase();
        if (src) setIntegrationSource(src);
      } catch (_) {}
    };
    fetchIntegrationSource();
  }, []);

  // Origem (Meta, link, SMS, ligação) vem no próprio pedido (`origem_campanha`,
  // enriquecido pelo servidor) — antes era um mapa à parte que só sabia o nome
  // e rotulava tudo como "SMS", inclusive link de divulgação (achado 400).
  // Declarado antes de hasActiveFilters, que agora também olha a Origem.
  const [filterOrigens, setFilterOrigens] = useState([]);

  // Declarado aqui pois é usado pelos useEffects de fetchPedidos paginado
  const hasActiveFilters = searchTerm || (filterStatuses && filterStatuses.length > 0) || filterModules.length > 0 ||
    filterTiposEntrega.length > 0 ||
    (filterPayments && filterPayments.length > 0) || filterDateFrom || filterDateTo || filterCaixaId || filterCaixa !== "todos" || (filterEntregadores && filterEntregadores.length > 0) ||
    filterOrigens.length > 0;

  // Buscar produtos para fallback de fotos
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const tok = localStorage.getItem("token");
        const res = await axios.get(`${API}/products`, { headers: tok ? { Authorization: `Bearer ${tok}` } : {} });
        const map = {};
        (res.data || []).forEach(p => {
          if (p.photo_url) {
            if (p.name) map[p.name.toLowerCase()] = p.photo_url;
            if (p.id) map[`id_${p.id}`] = p.photo_url;
          }
        });
        setProductsMap(map);
        setProdutosCadastro(Array.isArray(res.data) ? res.data : []);
      } catch (_) {}
    };
    fetchProducts();
  }, []);

  // Buscar lista de caixas para o filtro
  useEffect(() => {
    const fetchCaixas = async () => {
      try {
        const tok = localStorage.getItem("token");
        const res = await axios.get(`${API}/caixas`, { headers: { Authorization: `Bearer ${tok}` } });
        setCaixasList(res.data || []);
      } catch (_) {}
    };
    fetchCaixas();
  }, []);

  // De qual campanha de SMS cada pedido veio ({pedido_id: nome}). Mapa leve,
  // carregado uma vez — o badge aparece no card e no popup de detalhes.
  // Formas de pagamento cadastradas pela loja: o pedido guarda o código
  // ("cartao_<id da loja>") e aqui viram nome de gente ("Cartão").
  const [metodosLoja, setMetodosLoja] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const tok = localStorage.getItem("token");
        const res = await axios.get(`${API}/payment-methods`, { headers: tok ? { Authorization: `Bearer ${tok}` } : {} });
        const mapa = {};
        for (const m of res.data || []) {
          if (m?.code) mapa[m.code] = m;
          if (m?.id) mapa[m.id] = m;
        }
        setMetodosLoja(mapa);
      } catch (_) { /* loja sem formas customizadas */ }
    })();
  }, []);
  const nomePagamento = useCallback((v) => rotuloPagamento(v, metodosLoja), [metodosLoja]);
  const emojiDe = useCallback((forma) => emojiPagamento(forma, metodosLoja), [metodosLoja]);
  const opcoesPagamento = useOpcoesPagamento(API);


  // Sync URL param with filterCaixa
  useEffect(() => {
    if (filterCaixaId) setFilterCaixa(filterCaixaId);
  }, [filterCaixaId]);

  // Deep-link pra abrir o modal de detalhes direto via ?openPedido={id}.
  // Usado pelo Relatório de Avaliações (clicar na avaliação reusa esse modal).
  // Se o pedido não está na lista carregada (fora do filtro/paginação atual),
  // busca pelo endpoint individual e mapeia o payload flat → aninhado igual
  // o fetchPedidos faz. Limpa o param depois de abrir pra não reabrir em F5.
  useEffect(() => {
    const openId = searchParams.get("openPedido");
    if (!openId) return;
    const localMatch = pedidos.find((p) => p.id === openId);
    const open = (pedido) => {
      setSelectedPedido(pedido);
      setDetailsOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("openPedido");
      setSearchParams(next, { replace: true });
    };
    if (localMatch) {
      open(localMatch);
      return;
    }
    const token = localStorage.getItem("token");
    axios
      .get(`${API}/pedidos/${openId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => {
        if (res?.data) open(mapPedidoFromApi(res.data));
      })
      .catch(() => {});
  }, [searchParams, pedidos, setSearchParams]);

  // Reset para pagina 1 quando trocar itens por pagina
  useEffect(() => { setCurrentPage(1); }, [itemsPerPage]);

  // Fetch + listeners + intervalo (recriado a cada mudanca de paginacao/filtros para nao usar closure stale)
  useEffect(() => {
    fetchPedidos();
    const handlePedidosUpdate = () => fetchPedidos();
    window.addEventListener('pedidosUpdated', handlePedidosUpdate);
    const interval = setInterval(fetchPedidos, 10000);
    return () => {
      window.removeEventListener('pedidosUpdated', handlePedidosUpdate);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, hasActiveFilters, debouncedSearchTerm, filterDateFrom, filterDateTo, filterTimeFrom, filterTimeTo]);

  const fetchPedidos = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // Buscar stats agregadas em paralelo (sem bloquear a lista)
      axios.get(`${API}/pedidos/stats`, { headers })
        .then(r => setServerStats(r.data))
        .catch(() => {});
      // Qualquer filtro ativo (busca textual OU data) vai pelo /pedidos/search,
      // que varre o banco inteiro do tenant — sem cap de 500. Demais filtros
      // (status/módulo/pagamento/entregador) continuam aplicados client-side em
      // cima do resultado, então não precisa propagar pro servidor.
      let response;
      const effectiveSearch = (debouncedSearchTerm || "").trim();
      const useServerSearch = effectiveSearch || filterDateFrom || filterDateTo;
      if (useServerSearch) {
        const params = { limit: 5000 };
        if (effectiveSearch) params.q = effectiveSearch;
        if (filterDateFrom) {
          params.date_from = `${filterDateFrom}T${filterTimeFrom || '00:00'}:00-03:00`;
        }
        if (filterDateTo) {
          params.date_to = `${filterDateTo}T${filterTimeTo || '23:59'}:59-03:00`;
        }
        response = await axios.get(`${API}/pedidos/search`, { headers, params });
      } else {
        // Paginação server-side quando NÃO houver filtros ativos.
        // Com filtros (sem busca textual), carrega 500 mais recentes e filtra client-side.
        const params = !hasActiveFilters
          ? { limit: itemsPerPage * 4 + 10, offset: (currentPage - 1) * itemsPerPage }
          : { limit: 500 };
        response = await axios.get(`${API}/pedidos`, { headers, params });
      }
      
      // Transformar dados do backend (flat) → estrutura aninhada esperada pelo modal.
      // Helper extraído pra reusar no deep-link via ?openPedido={id}.
      const pedidosFormatados = response.data.map(mapPedidoFromApi);
      
      // Consolidar pedidos de Mesa: agrupar por sessão
      // Se mesa_opened_at disponível, usar como chave (sessão exata)
      // Senão, usar mesa_id + data local do created_at (fallback para pedidos antigos)
      const mesaGroups = {};
      const nonMesa = [];
      pedidosFormatados.forEach(p => {
        const isMesa = (p.modulo === 'Mesas' || p.mesa_id) && p.mesa_id;
        if (isMesa) {
          let sessionKey;
          if (p.mesa_opened_at) {
            sessionKey = `${p.mesa_id}_session_${p.mesa_opened_at}`;
          } else {
            const d = new Date(p.created_at);
            const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
            sessionKey = `${p.mesa_id}_date_${dateStr}`;
          }
          if (!mesaGroups[sessionKey]) {
            mesaGroups[sessionKey] = { ...p, items: [...(p.items || [])], _merged_ids: [p.id], _rodadas: [p] };
          } else {
            mesaGroups[sessionKey]._rodadas.push(p);
            mesaGroups[sessionKey].items.push(...(p.items || []));
            mesaGroups[sessionKey].total = (mesaGroups[sessionKey].total || 0) + (p.total || 0);
            mesaGroups[sessionKey]._merged_ids.push(p.id);
            if (p.formaPagamento && p.formaPagamento !== '—') mesaGroups[sessionKey].formaPagamento = p.formaPagamento;
            if (new Date(p.created_at) > new Date(mesaGroups[sessionKey].created_at)) {
              mesaGroups[sessionKey].created_at = p.created_at;
            }
            if (p.mesa_opened_at && !mesaGroups[sessionKey].mesa_opened_at) mesaGroups[sessionKey].mesa_opened_at = p.mesa_opened_at;
            if (p.status === 'concluido') mesaGroups[sessionKey].status = p.status;
            else if (p.status === 'cancelado' && mesaGroups[sessionKey].status !== 'concluido') mesaGroups[sessionKey].status = p.status;
          }
        } else {
          nonMesa.push(p);
        }
      });
      // Formas da sessão = soma das rodadas (cada pedido grava a parte dele).
      // Rodada cancelada não entra, a não ser que a sessão inteira tenha sido.
      Object.values(mesaGroups).forEach((g) => {
        const validas = g._rodadas.filter((r) => r.status !== 'cancelado');
        g.pagamentoPartes = juntarPartes((validas.length ? validas : g._rodadas).map(partesDoPedido));
        delete g._rodadas;
      });
      const consolidated = [...nonMesa, ...Object.values(mesaGroups)];
      consolidated.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      // Sem filtros, fatiar para itemsPerPage exato (ja vem a pagina atual do servidor + buffer)
      const finalList = !hasActiveFilters ? consolidated.slice(0, itemsPerPage) : consolidated;
      setPedidos(finalList);
    } catch (error) {
      console.error("Erro ao buscar pedidos:", error);
    } finally {
      setLoading(false);
    }
  };

  const updatePedidoStatus = async (pedidoId, newStatus) => {
    // ATUALIZAÇÃO OTIMISTA: muda UI imediatamente; rollback em caso de erro.
    const pedidoAnterior = pedidos.find(p => p.id === pedidoId);
    const statusAnterior = pedidoAnterior?.status;
    setPedidos(prev => prev.map(p => p.id === pedidoId ? { ...p, status: newStatus } : p));
    toast.success("Status atualizado!");
    try {
      // Com o login da loja (achado 23, 27/10/2026): o backend passou a exigir
      const tok = localStorage.getItem("token");
      await axios.patch(`${API}/pedidos/${pedidoId}/status?status=${newStatus}`, {}, { headers: tok ? { Authorization: `Bearer ${tok}` } : {} });
    } catch (error) {
      if (statusAnterior !== undefined) {
        setPedidos(prev => prev.map(p => p.id === pedidoId ? { ...p, status: statusAnterior } : p));
      }
      console.error("Erro ao atualizar status:", error);
      toast.error("Erro ao atualizar status");
    }
  };

  // Formatar data e hora (com segundos para o tempo certinho)
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("pt-BR") + " " + date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  // Formatar tempo de etapa: total em segundos → "X min Y s" ou "X min" ou "Y s" (tempo certinho)
  const formatTempoEtapa = (seg) => {
    if (seg == null || seg === undefined) return "—";
    const m = Math.floor(seg / 60);
    const s = Math.floor(seg % 60);
    if (m === 0) return s === 0 ? "0 s" : `${s} s`;
    if (s === 0) return `${m} min`;
    return `${m} min ${s} s`;
  };

  // Formatar tempo de permanência (abertura → fechamento)
  const formatPermanencia = (openedAt, closedAt) => {
    if (!openedAt || !closedAt) return "—";
    const min = (new Date(closedAt) - new Date(openedAt)) / 60000;
    if (min < 1) return "< 1 min";
    if (min < 60) return `${Math.round(min)} min`;
    const h = Math.floor(min / 60);
    const m = Math.round(min % 60);
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  };

  // Filtrar pedidos - MEMOIZADO
  const filteredPedidos = useMemo(() => {
    return pedidos
      .filter(p => {
        // Filtro por status (multi-select)
        if (filterStatuses.length > 0) {
          const normalizedStatus = p.status === "aguardando" ? "producao" : p.status;
          if (!filterStatuses.includes(normalizedStatus)) return false;
        }

        // Filtro por módulo (multi-select, Cardapio inclui CardapioPublico)
        // "CLEO" não é módulo: é o pedido que a CLEO anotou pela conversa (entra como Cardápio).
        if (filterModules.length > 0) {
          const mod = p.modulo === "CardapioPublico" ? "Cardapio" : p.modulo;
          if (!filterModules.includes(mod) && !(p.origem === "cleo" && filterModules.includes(FILTRO_CLEO))) return false;
        }

        // Variação do canal (entrega x retirada x mesa). Vem de clicar numa
        // linha dentro do card do canal, no resumo.
        if (filterTiposEntrega.length > 0) {
          if (!filterTiposEntrega.includes(p.tipo_entrega || "")) return false;
        }

        // Filtro por forma de pagamento (multi-select): pedido dividido entra
        // em cada forma usada (50/50 aparece em Dinheiro e em Cartão)
        if (filterPayments.length > 0) {
          if (!casaFiltroPagamento(partesDoPedido(p), filterPayments)) return false;
        }

        // Filtro por período (data + hora opcional, sempre em horário de São Paulo / UTC-3).
        // Sem hora: limites são 00:00 (início) e 23:59 (fim) do dia escolhido.
        // Com hora: usa exatamente o HH:MM informado.
        if (filterDateFrom || filterDateTo) {
          const pedidoMs = new Date(p.created_at).getTime();
          if (filterDateFrom) {
            const t = filterTimeFrom || "00:00";
            const fromMs = new Date(`${filterDateFrom}T${t}:00-03:00`).getTime();
            if (pedidoMs < fromMs) return false;
          }
          if (filterDateTo) {
            const t = filterTimeTo || "23:59";
            const toMs = new Date(`${filterDateTo}T${t}:59-03:00`).getTime();
            if (pedidoMs > toMs) return false;
          }
        }

        // Filtro por caixa (via URL ou dropdown)
        const activeCaixaFilter = filterCaixa !== "todos" ? filterCaixa : filterCaixaId;
        if (activeCaixaFilter) {
          if (String(p.caixa_id) !== String(activeCaixaFilter)) return false;
        }

        // Filtro por entregador (multi-select)
        if (filterEntregadores.length > 0) {
          if (!filterEntregadores.includes(p.entregador_nome || "")) return false;
        }

        // Filtro por origem da campanha (Meta, link, SMS, ligação, nenhuma)
        if (filterOrigens.length > 0) {
          const canal = p.origem_campanha?.canal || "nenhuma";
          if (!filterOrigens.includes(canal)) return false;
        }

        // Filtro por busca (texto) — espelha o que /pedidos/search faz no servidor:
        // código (com ou sem #), nome/telefone/email do cliente, nome do produto nos items,
        // observação e senha do balcão. Como a chamada de search no servidor já retornou só
        // matches, aqui o filtro vira praticamente um pass-through. Mantemos a checagem
        // pra cobrir cenários em que houver busca + outros filtros aplicados sem refetch.
        if (searchTerm) {
          const search = searchTerm.toLowerCase().trim();
          const searchNoHash = search.replace(/^#/, "");
          const codigo = (p.codigo || "").toLowerCase();
          const matchHeader = (
            codigo.includes(search) ||
            codigo.includes(searchNoHash) ||
            p.cliente?.nome?.toLowerCase().includes(search) ||
            (p.cliente?.telefone || "").includes(search) ||
            p.cliente?.email?.toLowerCase().includes(search) ||
            (p.observacao || "").toLowerCase().includes(search) ||
            (p.senha_balcao || "").toLowerCase().includes(search)
          );
          if (matchHeader) return true;
          const matchItem = (p.items || []).some((it) => {
            const nome = (it.nome || it.name || "").toLowerCase();
            const obs = (it.observacao || it.observation || "").toLowerCase();
            return nome.includes(search) || obs.includes(search);
          });
          return matchItem;
        }
        return true;
      })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  // filterCaixa, filterTiposEntrega e filterOrigens faltavam aqui: escolher só a
  // Origem não refiltrava a lista até o próximo refresh de 10 s (25/10/2026).
  }, [pedidos, filterStatuses, filterModules, filterTiposEntrega, filterPayments, filterDateFrom, filterDateTo, filterTimeFrom, filterTimeTo, searchTerm, filterCaixa, filterCaixaId, filterEntregadores, filterOrigens]);

  // Pedidos paginados — quando há filtros, fatia client-side; sem filtros, usa direto a página vinda do servidor
  const paginatedPedidos = useMemo(() => {
    if (hasActiveFilters) {
      const startIndex = (currentPage - 1) * itemsPerPage;
      return filteredPedidos.slice(startIndex, startIndex + itemsPerPage);
    }
    return filteredPedidos;
  }, [filteredPedidos, currentPage, itemsPerPage, hasActiveFilters]);

  // Total efetivo de itens para a paginação: filtrado quando há filtros, total do servidor quando não
  const effectiveTotal = !hasActiveFilters && serverStats ? (serverStats.total || 0) : filteredPedidos.length;

  // Reset página quando filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatuses, filterModules, filterTiposEntrega, filterPayments, filterDateFrom, filterDateTo, filterCaixa, filterEntregadores, filterOrigens]);

  // Abrir detalhes do pedido
  const handleOpenDetails = (pedido) => {
    setSelectedPedido(pedido);
    setDetailsOpen(true);
  };

  // Alterar status do pedido
  const handleChangeStatus = async (pedidoId, newStatus) => {
    try {
      await updatePedidoStatus(pedidoId, newStatus);
      
      // Atualizar pedido selecionado se estiver aberto
      if (selectedPedido?.id === pedidoId) {
        setSelectedPedido({ ...selectedPedido, status: newStatus });
      }
      
      toast.success(`Status alterado para ${statusConfig[newStatus]?.label || newStatus}`);
    } catch (error) {
      console.error("Erro ao alterar status:", error);
    }
  };

  // Cancela em lote os pedidos da sessão de mesa consolidada (ou um único pedido).
  // Sem isso, o frontend cancelava só o "wrapper" e a consolidação revertia para concluído
  // ao ver outros rounds da mesa ainda como concluído.
  const cancelPedidosBulk = async (pedidoIds) => {
    if (!pedidoIds || pedidoIds.length === 0) return;
    // Otimista: marca todos como cancelado na lista e no selectedPedido
    setPedidos(prev => prev.map(p => pedidoIds.includes(p.id) ? { ...p, status: "cancelado" } : p));
    if (selectedPedido && pedidoIds.includes(selectedPedido.id)) {
      setSelectedPedido({ ...selectedPedido, status: "cancelado" });
    }
    // iFood exige motivo da lista dele: vai pela rota /cancelar com o código.
    // Login vai junto (v1.5.789): cancelar mexe no caixa (estorno) e o servidor
    // exige usuário da loja — antes o /cancelar do iFood voltava 403 daqui.
    const auth = { headers: { Authorization: `Bearer ${getToken()}` } };
    const results = await Promise.allSettled(
      pedidoIds.map(pid => isIfoodSelected
        ? axios.patch(`${API}/pedidos/${pid}/cancelar`, {
            motivo: ifoodCancelReason?.description || "Cancelado pela loja",
            ifood_cancel_code: ifoodCancelReason?.code,
          }, auth)
        : axios.patch(`${API}/pedidos/${pid}/status?status=cancelado`, {}, auth))
    );
    const falhas = results.filter(r => r.status === "rejected");
    const fails = falhas.length;
    if (fails > 0) {
      // "Pedido de caixa já fechado não pode ser cancelado" e afins: mostra o motivo.
      const motivo = falhas[0].reason?.response?.data?.detail;
      toast.error(typeof motivo === "string" ? motivo : `Falha em ${fails} de ${pedidoIds.length} pedidos`, { duration: 8000 });
      // Re-sincroniza com o backend pra refletir o estado real
      fetchPedidos();
    } else {
      toast.success(pedidoIds.length > 1
        ? `${pedidoIds.length} pedidos da sessão cancelados!`
        : "Pedido cancelado com sucesso!");
    }
  };

  // Funcionário com a permissão "Cancelar pedido" confirma com a própria senha;
  // sem ela, só com a do administrador. Dono e administrador, como sempre, com a deles.
  const cancelaComASenhaDele = cancelDialogOpen && (() => {
    const eu = usuarioLogado();
    return !temAcessoTotal(eu) && temPermissao(eu, PERMISSAO_CANCELAR);
  })();
  const SENHA_ERRADA = cancelaComASenhaDele
    ? "Senha incorreta. Digite a sua senha."
    : "Senha incorreta. Digite a senha do administrador.";

  // Cancelar pedido com validação de senha (do administrador ou de quem tem a permissão)
  const handleCancelPedido = async () => {
    if (!selectedPedido) return;

    setCancelError("");
    if (isIfoodSelected && !ifoodCancelReason?.code) {
      setCancelError("Escolha o motivo do cancelamento na lista do iFood.");
      return;
    }

    // Sessões de mesa consolidadas têm múltiplos pedidos no _merged_ids — cancela todos.
    const idsToCancel = (Array.isArray(selectedPedido._merged_ids) && selectedPedido._merged_ids.length > 0)
      ? selectedPedido._merged_ids
      : [selectedPedido.id];

    // Validar senha do admin
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username: "admin",
        password: adminPassword
      });

      if (response.data.access_token) {
        await cancelPedidosBulk(idsToCancel);
        setCancelDialogOpen(false);
        setAdminPassword("");
      }
    } catch (error) {
      // Não é a senha do "admin": vale a de quem está logado, se for dono,
      // administrador ou funcionário com a permissão "Cancelar pedido".
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const userStr = localStorage.getItem("user");
          const user = userStr ? JSON.parse(userStr) : null;

          if (temPermissao(user, PERMISSAO_CANCELAR)) {
            const loginResp = await axios.post(`${API}/auth/login`, {
              username: user.username,
              password: adminPassword
            });

            if (loginResp.data.access_token) {
              await cancelPedidosBulk(idsToCancel);
              setCancelDialogOpen(false);
              setAdminPassword("");
              return;
            }
          }
        }
        setCancelError(SENHA_ERRADA);
      } catch (e) {
        setCancelError(SENHA_ERRADA);
      }
    }
  };

  // Reimprimir cupom (2ª via) — busca pedido completo na API
  // type: 'auto' (default), 'ambos', 'entrega', 'preparo'
  const handleReimprimirCupom = async (pedidoOverride, type = 'auto') => {
    const pedidoRef = pedidoOverride || selectedPedido;
    if (!pedidoRef?.id) return;
    try {
      // Pedido com nota emitida reimprime pelo backend, que é quem sabe montar
      // o bloco DANFE (chave, protocolo, QR Code). Sem nota, segue o fluxo
      // normal de reimpressão local.
      if (type === 'auto' || type === 'entrega') {
        if (await reimprimirComNota(pedidoRef.id)) return;
      }
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/pedidos/${pedidoRef.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const pedido = res.data;

      // Para pedidos de Mesa, usar cupom de fechamento
      if (pedido.modulo === 'Mesas' || pedido.tipo_entrega === 'mesa') {
        const mesaNumMatch = (pedido.endereco_rua || '').match(/Mesa\s*(\d+)/i);
        const mesaNum = mesaNumMatch ? mesaNumMatch[1] : '?';
        const mesa = {
          numero: mesaNum,
          comanda: pedido.observacao?.replace('Comanda: ', '') || null,
          codigo: null,
          opened_at: pedido.created_at,
          closed_at: pedido.ts_concluido || pedido.updated_at,
          cliente: pedido.cliente_nome || '—',
          total: pedido.total,
          items: null,
        };
        // Formas com nome e valor: sem isto o cupom saía "PAGAMENTO: multiplo"
        const partes = pedido.pagamento_partes || [];
        await imprimirCupomFechamento(mesa, [pedido], partes.length === 1 ? partes[0].nome : pedido.forma_pagamento,
          partes.length > 1 ? partes.map((pt) => ({ label: pt.nome, value: pt.valor })) : undefined,
          undefined, { agrupar: false });
      } else if (type === 'ambos') {
        await Promise.all([imprimirCupom(pedido), imprimirCupomPreparo(pedido)]);
      } else if (type === 'entrega') {
        await imprimirCupom(pedido);
      } else if (type === 'preparo') {
        await imprimirCupomPreparo(pedido);
      } else {
        await imprimirCupom(pedido);
      }
      toast.success("Cupom reimpresso!");
    } catch (err) {
      toast.error(err?.message || "Erro ao reimprimir cupom");
    }
  };

  // Aprovar pedido agendado: muda status pra 'aguardando_aceite' + dispara impressão.
  // Backend faz tudo atomicamente (POST /api/pedidos/{id}/aprovar-agendado).
  const handleAprovarAgendado = async (pedido, e) => {
    if (e) e.stopPropagation();
    if (!pedido?.id) return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/pedidos/${pedido.id}/aprovar-agendado`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      toast.success(`Pedido ${pedido.codigo || ""} aprovado e enviado pra produção!`);
      // Refetch pra atualizar status na UI
      window.dispatchEvent(new Event("pedidosUpdated"));
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Erro ao aprovar pedido agendado";
      toast.error(detail);
    }
  };

  // Formata scheduled_at ("2026-07-05T19:00:00") como janela de 30min
  // ("Sáb 5 · 19:00–19:30") pra dar margem de erro no preparo/entrega.
  const fmtScheduledAt = (iso) => {
    if (!iso) return "";
    try {
      const [d, hm] = String(iso).split("T");
      const [y, mo, da] = d.split("-").map((n) => parseInt(n, 10));
      const [hh, mm] = (hm || "00:00").split(":");
      const dt = new Date(y, mo - 1, da);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const diff = Math.round((dt - today) / (1000 * 60 * 60 * 24));
      let dayLbl;
      if (diff === 0) dayLbl = "Hoje";
      else if (diff === 1) dayLbl = "Amanhã";
      else {
        const wd = dt.toLocaleDateString("pt-BR", { weekday: "short" }).replace(/\.$/, "");
        dayLbl = `${wd.charAt(0).toUpperCase() + wd.slice(1)} ${da}`;
      }
      const startMinutes = parseInt(hh, 10) * 60 + parseInt(mm, 10);
      const endMinutes = startMinutes + 30;
      const eh = String(Math.floor(endMinutes / 60) % 24).padStart(2, "0");
      const em = String(endMinutes % 60).padStart(2, "0");
      return `${dayLbl} · ${hh}:${mm}–${eh}:${em}`;
    } catch { return iso; }
  };

  // ===== Resumo do servidor (painel da direita) =====
  // Os MESMOS filtros da tela viram query params, então faturamento, ticket e
  // origem falam do conjunto filtrado inteiro. O `stats` abaixo continua
  // existindo pro que é da página carregada (badges da lista).
  const filtrosResumo = useMemo(() => {
    const f = {};
    const busca = (debouncedSearchTerm || "").trim();
    if (busca) f.q = busca;
    if (filterDateFrom) f.date_from = `${filterDateFrom}T${filterTimeFrom || "00:00"}:00-03:00`;
    if (filterDateTo) f.date_to = `${filterDateTo}T${filterTimeTo || "23:59"}:59-03:00`;
    if (filterStatuses.length > 0) f.status = filterStatuses.join(",");
    if (filterModules.length > 0) f.modulo = filterModules.join(",");
    if (filterTiposEntrega.length > 0) f.tipo_entrega = filterTiposEntrega.join(",");
    if (filterPayments.length > 0) f.forma_pagamento = filterPayments.join(",");
    const caixaAtivo = filterCaixa !== "todos" ? filterCaixa : filterCaixaId;
    if (caixaAtivo) f.caixa_id = caixaAtivo;
    if (filterEntregadores.length > 0) f.entregador = filterEntregadores.join(",");
    if (filterOrigens.length > 0) f.origem_campanha = filterOrigens.join(",");
    return f;
  }, [debouncedSearchTerm, filterDateFrom, filterDateTo, filterTimeFrom, filterTimeTo,
      filterStatuses, filterModules, filterTiposEntrega, filterPayments, filterCaixa,
      filterCaixaId, filterEntregadores, filterOrigens]);

  const { stats: resumo, carregando: carregandoResumo } = useResumoPedidos(filtrosResumo);

  const [resumoAberto, setResumoAberto] = useState(() => {
    try { return localStorage.getItem("pedidos_resumo_aberto") !== "0"; } catch { return true; }
  });
  const alternarResumo = useCallback(() => {
    setResumoAberto((atual) => {
      try { localStorage.setItem("pedidos_resumo_aberto", atual ? "0" : "1"); } catch { /* ok */ }
      return !atual;
    });
  }, []);

  // Canais de venda agrupados, com variações, ticket e participação. A conta
  // vem do breakdown do servidor, então vale pelo conjunto filtrado inteiro.
  const grupos = useMemo(() => montarGrupos(resumo?.breakdown), [resumo]);
  const automacao = useMemo(() => calcularAutomacao(resumo?.breakdown), [resumo]);

  const [grupoAberto, setGrupoAberto] = useState(null);
  const alternarGrupo = useCallback((chave) => {
    // Sanfona: um canal aberto por vez, pra a coluna caber na altura da tela.
    setGrupoAberto((atual) => (atual === chave ? null : chave));
  }, []);

  const [variacaoAtiva, setVariacaoAtiva] = useState(null);
  const filtrarPorVariacao = useCallback((variacao) => {
    const mesma = variacaoAtiva === variacao.key;
    setVariacaoAtiva(mesma ? null : variacao.key);
    setFilterModules(mesma ? [] : (variacao.filtro.modulo || []));
    setFilterTiposEntrega(mesma ? [] : (variacao.filtro.tipo_entrega || []));
  }, [variacaoAtiva]);


  // Obter estatísticas financeiras — baseado nos pedidos FILTRADOS
  const stats = useMemo(() => {
    if (!hasActiveFilters && serverStats) return serverStats;
    const base = filteredPedidos;
    // Agendados NÃO contam no faturamento até o lojista aprovar (viram
    // 'aguardando_aceite' → produção). Excluídos junto com cancelados.
    const pedidosNaoCancelados = base.filter(p => p.status !== "cancelado" && p.status !== "agendado");
    const pedidosFinalizados = pedidosNaoCancelados.filter(p => p.status === "concluido");

    // Faturamento (dos filtrados, não cancelados)
    const faturamentoTotal = pedidosNaoCancelados.reduce((sum, p) => sum + (parseFloat(p.total) || 0), 0);

    // Faturamento hoje (dentro dos filtrados)
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const pedidosHoje = pedidosNaoCancelados.filter(p => {
      if (!p.created_at) return false;
      const dataPedido = new Date(p.created_at);
      dataPedido.setHours(0, 0, 0, 0);
      return dataPedido.getTime() === hoje.getTime();
    });
    const faturamentoHoje = pedidosHoje.reduce((sum, p) => sum + (parseFloat(p.total) || 0), 0);

    // Ticket médio
    const ticketMedio = pedidosFinalizados.length > 0
      ? faturamentoTotal / pedidosFinalizados.length
      : 0;

    // Distribuição por módulo (dos filtrados, não cancelados).
    // Pedidos de integrações externas (Grandchef, AnotaAi, ...) entram nas contagens
    // próprias mas NÃO compõem o moduleTotal usado no cálculo de % dos demais módulos.
    const moduleCounts = {};
    const moduleRevenue = {};
    const pedidosComModulo = pedidosNaoCancelados.filter(p => p.modulo);
    pedidosComModulo.forEach(p => {
      const mod = p.modulo === 'CardapioPublico' ? 'Cardapio' : p.modulo;
      moduleCounts[mod] = (moduleCounts[mod] || 0) + 1;
      moduleRevenue[mod] = (moduleRevenue[mod] || 0) + (parseFloat(p.total) || 0);
    });
    const moduleTotal = pedidosComModulo.filter(p => !INTEGRATION_MODULES.includes(p.modulo)).length;

    return {
      faturamentoTotal,
      faturamentoHoje,
      ticketMedio,
      total: base.length,
      producao: base.filter(p => p.status === "producao" || p.status === "aguardando").length,
      transito: base.filter(p => p.status === "transito").length,
      concluido: base.filter(p => p.status === "concluido").length,
      cancelado: base.filter(p => p.status === "cancelado").length,
      agendado: base.filter(p => p.status === "agendado").length,
      moduleCounts,
      moduleRevenue,
      moduleTotal,
    };
    // serverStats chega depois da lista: sem ele aqui, o aviso de agendados
    // ficava com a conta da página até o refresh seguinte.
  }, [filteredPedidos, hasActiveFilters, serverStats]);

  // Normalizar status para exibição
  const getNormalizedStatus = (status) => {
    if (status === "aguardando") return "producao";
    return status;
  };

  // Exportar para Excel
  const handleExportExcel = () => {
    if (filteredPedidos.length === 0) {
      toast.error("Nenhum pedido para exportar");
      return;
    }

    // Preparar dados para exportação
    const exportData = filteredPedidos.map(p => ({
      "Código": p.codigo || "",
      "Data/Hora": p.created_at ? new Date(p.created_at).toLocaleString("pt-BR") : "",
      "Cliente": p.cliente?.nome || "",
      "Telefone": p.cliente?.telefone || "",
      "Módulo": modulosConfig[p.modulo]?.label || p.modulo || "",
      "Status": statusConfig[getNormalizedStatus(p.status)]?.label || p.status || "",
      "Tipo Entrega": p.tipoEntrega === "pickup" ? "Retirada" : p.tipoEntrega === "delivery" ? "Entrega" : "",
      "Endereço": p.endereco ? `${p.endereco.endereco || ""}, ${p.endereco.numero || ""} - ${p.endereco.bairro || ""}` : "",
      "Forma Pagamento": textoPartes(partesDoPedido(p)),
      "Qtd Itens": p.items?.length || 0,
      "Total (R$)": (p.total || 0).toFixed(2).replace(".", ","),
      "Entregador": p.entregador_nome || "",
      "Observação": p.observacao || "",
    }));

    // Criar workbook e worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Ajustar largura das colunas
    const colWidths = [
      { wch: 12 },  // Código
      { wch: 18 },  // Data/Hora
      { wch: 25 },  // Cliente
      { wch: 15 },  // Telefone
      { wch: 15 },  // Módulo
      { wch: 15 },  // Status
      { wch: 12 },  // Tipo Entrega
      { wch: 40 },  // Endereço
      { wch: 15 },  // Forma Pagamento
      { wch: 10 },  // Qtd Itens
      { wch: 12 },  // Total
      { wch: 20 },  // Entregador
      { wch: 30 },  // Observação
    ];
    ws["!cols"] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, "Pedidos");

    // Gerar nome do arquivo com data
    const hoje = new Date().toISOString().split("T")[0];
    const fileName = `pedidos_${hoje}.xlsx`;

    // Download
    XLSX.writeFile(wb, fileName);
    toast.success(`Exportados ${filteredPedidos.length} pedidos para Excel`);
  };

  // Limpar todos os filtros
  const handleClearFilters = () => {
    setSearchTerm("");
    setFilterStatuses([]);
    setFilterModules([]);
    setFilterTiposEntrega([]);
    setVariacaoAtiva(null);
    setFilterPayments([]);
    setFilterCaixa("todos");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterTimeFrom("");
    setFilterTimeTo("");
    setFilterEntregadores([]);
    // Faltava: "Limpar tudo" deixava o filtro de Origem ligado (25/10/2026).
    setFilterOrigens([]);
    if (filterCaixaId) {
      searchParams.delete("caixa_id");
      setSearchParams(searchParams);
    }
  };

  // Atalhos rápidos de data
  const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const setDatePreset = (preset) => {
    const today = new Date();
    const todayStr = fmtDate(today);
    // Presets sempre zeram a hora (dia inteiro) e fecham o bloco custom
    if (preset !== 'custom') {
      setFilterTimeFrom("");
      setFilterTimeTo("");
      setCustomDateOpen(false);
    }
    if (preset === 'hoje') { setFilterDateFrom(todayStr); setFilterDateTo(todayStr); }
    else if (preset === 'ontem') { const y = new Date(today); y.setDate(y.getDate()-1); const s = fmtDate(y); setFilterDateFrom(s); setFilterDateTo(s); }
    else if (preset === '7dias') { const d = new Date(today); d.setDate(d.getDate()-7); setFilterDateFrom(fmtDate(d)); setFilterDateTo(todayStr); }
    else if (preset === 'mes') { const m = new Date(today.getFullYear(), today.getMonth(), 1); setFilterDateFrom(fmtDate(m)); setFilterDateTo(todayStr); }
    else if (preset === 'custom') {
      setCustomDateOpen(true);
      // Carrega o rascunho com o que estiver aplicado (ou defaults sensatos).
      // O filtro só aplica ao clicar em "Aplicar filtro" dentro do bloco custom.
      if (!filterDateFrom && !filterDateTo) {
        setPendingDateFrom(todayStr);
        setPendingDateTo(todayStr);
        setPendingTimeFrom("00:00");
        setPendingTimeTo("23:59");
      } else {
        setPendingDateFrom(filterDateFrom || "");
        setPendingDateTo(filterDateTo || "");
        setPendingTimeFrom(filterTimeFrom || (filterDateFrom ? "00:00" : ""));
        setPendingTimeTo(filterTimeTo || (filterDateTo ? "23:59" : ""));
      }
    }
  };
  const getActiveDatePreset = () => {
    if (customDateOpen) return 'custom';
    const today = new Date();
    const todayStr = fmtDate(today);
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate()-1); const yesterdayStr = fmtDate(yesterday);
    const d7 = new Date(today); d7.setDate(d7.getDate()-7); const d7Str = fmtDate(d7);
    const monthStart = fmtDate(new Date(today.getFullYear(), today.getMonth(), 1));
    const noTimes = !filterTimeFrom && !filterTimeTo;
    if (noTimes && filterDateFrom === todayStr && filterDateTo === todayStr) return 'hoje';
    if (noTimes && filterDateFrom === yesterdayStr && filterDateTo === yesterdayStr) return 'ontem';
    if (noTimes && filterDateFrom === d7Str && filterDateTo === todayStr) return '7dias';
    if (noTimes && filterDateFrom === monthStart && filterDateTo === todayStr) return 'mes';
    if (filterDateFrom || filterDateTo) return 'custom';
    return null;
  };

  // O que está filtrado agora, em chips removíveis um a um.
  const chipsFiltros = useMemo(() => {
    const lista = [];
    const busca = (searchTerm || "").trim();
    if (busca) {
      lista.push({ id: "busca", rotulo: `"${busca}"`, onRemover: () => setSearchTerm("") });
    }
    if (filterDateFrom || filterDateTo) {
      const preset = getActiveDatePreset();
      const rotulos = { hoje: "Hoje", ontem: "Ontem", "7dias": "Últimos 7 dias", mes: "Este mês" };
      lista.push({
        id: "periodo",
        rotulo: rotulos[preset] || `${filterDateFrom || "…"} até ${filterDateTo || "…"}`,
        onRemover: () => {
          setFilterDateFrom(""); setFilterDateTo("");
          setFilterTimeFrom(""); setFilterTimeTo(""); setCustomDateOpen(false);
        },
      });
    }
    filterStatuses.forEach((s) => lista.push({
      id: `status-${s}`,
      rotulo: statusConfig[s]?.label || s,
      onRemover: () => setFilterStatuses((a) => a.filter((x) => x !== s)),
    }));
    // Clicar numa variação liga dois filtros (canal + tipo de entrega). Vira UM
    // chip só, senão o lojista remove metade do filtro sem querer.
    if (variacaoAtiva) {
      const dona = (grupos || []).flatMap((g) => g.variacoes || []).find((v) => v.key === variacaoAtiva);
      lista.push({
        id: "variacao",
        rotulo: dona?.label || "Variação",
        onRemover: () => {
          setVariacaoAtiva(null); setFilterTiposEntrega([]); setFilterModules([]);
        },
      });
    }
    if (!variacaoAtiva && filterTiposEntrega.length > 0) {
      const rotulos = { delivery: "Entregas", pickup: "Retiradas", mesa: "Mesas", balcao: "Balcão", local: "Balcão", viagem: "Viagem", levar_agora: "Levar agora", comanda: "Comandas" };
      lista.push({
        id: "tipo-entrega",
        rotulo: filterTiposEntrega.map((x) => rotulos[x] || x).join(" + "),
        onRemover: () => setFilterTiposEntrega([]),
      });
    }
    (variacaoAtiva ? [] : filterModules).forEach((m) => lista.push({
      id: `modulo-${m}`,
      rotulo: m === FILTRO_CLEO ? "CLEO (WhatsApp)" : MODULOS_RESUMO.find((x) => x.key === m)?.label || m,
      onRemover: () => setFilterModules((a) => a.filter((x) => x !== m)),
    }));
    filterPayments.forEach((fp) => lista.push({
      id: `pgto-${fp}`,
      rotulo: opcoesPagamento.find((o) => o.value === fp)?.label || fp,
      onRemover: () => setFilterPayments((a) => a.filter((x) => x !== fp)),
    }));
    filterEntregadores.forEach((e) => lista.push({
      id: `entregador-${e}`,
      rotulo: e || "Sem entregador",
      onRemover: () => setFilterEntregadores((a) => a.filter((x) => x !== e)),
    }));
    if (filterCaixa !== "todos") {
      lista.push({ id: "caixa", rotulo: `Caixa ${filterCaixa}`, onRemover: () => setFilterCaixa("todos") });
    }
    filterOrigens.forEach((o) => lista.push({
      id: `origem-${o}`,
      rotulo: OPCOES_ORIGEM.find((x) => x.value === o)?.label || o,
      onRemover: () => setFilterOrigens((a) => a.filter((x) => x !== o)),
    }));
    return lista;
  }, [searchTerm, filterDateFrom, filterDateTo, filterStatuses, filterModules,
      filterTiposEntrega, filterPayments, filterEntregadores, filterCaixa,
      variacaoAtiva, grupos, opcoesPagamento, filterOrigens]);


  // hasActiveFilters declarado acima (antes de stats)

  // Lista de entregadores únicos para o filtro
  const entregadoresList = useMemo(() => {
    const names = new Set();
    pedidos.forEach(p => { if (p.entregador_nome) names.add(p.entregador_nome); });
    return [...names].sort();
  }, [pedidos]);

  return (
    <div className="p-6">
      {/* Banner filtro por caixa */}
      {filterCaixaId && (
        <div className="mb-4 flex items-center gap-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg px-4 py-2">
          <DollarSign className="w-4 h-4 text-orange-500" />
          <span className="text-sm font-medium">Filtrando pedidos do Caixa #{filterCaixaId}</span>
          <Button variant="ghost" size="sm" className="ml-auto h-7 text-xs" onClick={() => { searchParams.delete("caixa_id"); setSearchParams(searchParams); }}>
            <X className="w-3 h-3 mr-1" /> Remover filtro
          </Button>
        </div>
      )}

      {/* Cabeçalho único atravessando o topo: título à esquerda e, colado nele,
          o cabeçalho do resumo à direita. O painel pendura direto embaixo, então
          os dois leem como uma peça só. */}
      <CabecalhoPedidos
        resumoAberto={resumoAberto}
        onAlternarResumo={alternarResumo}
        filtrado={hasActiveFilters}
        larguraPainel={LARGURA_PAINEL}
      />

      <div className="flex items-start">
        <div className="flex-1 min-w-0 pt-4 xl:pr-4">

      {/* Filtros numa linha só: busca, período e as caixas de seleção, todas
          com o mesmo desenho. O bloco de status virou parte do resumo à
          direita — pré-filtro de status ocupava a barra inteira. */}
      <div className="bg-card rounded-xl border shadow-sm mb-4 overflow-hidden">
        <div className="p-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar pedido ou cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-8 h-9"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <FiltroPeriodo
            presetAtivo={getActiveDatePreset()}
            dataDe={filterDateFrom}
            dataAte={filterDateTo}
            horaDe={filterTimeFrom}
            horaAte={filterTimeTo}
            dataMaisAntiga={resumo?.oldestDate}
            onPreset={(k) => setDatePreset(k)}
            onLimpar={() => {
              setFilterDateFrom(""); setFilterDateTo("");
              setFilterTimeFrom(""); setFilterTimeTo(""); setCustomDateOpen(false);
            }}
            onAplicarPersonalizado={({ de, ate, hDe, hAte }) => {
              setFilterDateFrom(de || ate);
              setFilterDateTo(ate || de);
              setFilterTimeFrom(hDe || "");
              setFilterTimeTo(hAte || "");
              setCustomDateOpen(true);
            }}
          />

          <FiltroMulti
            e2e="filtro-status"
            icone={Filter}
            rotulo="Status"
            opcoes={OPCOES_STATUS}
            selecionados={filterStatuses}
            onToggle={(valor, marcado) => setFilterStatuses((prev) =>
              marcado ? [...prev, valor] : prev.filter((v) => v !== valor))}
            onLimpar={() => setFilterStatuses([])}
          />

          <FiltroMulti
            e2e="filtro-pagamento"
            icone={CreditCard}
            rotulo="Pagamento"
            opcoes={opcoesPagamento}
            selecionados={filterPayments}
            onToggle={(valor, marcado) => setFilterPayments((prev) =>
              marcado ? [...prev, valor] : prev.filter((v) => v !== valor))}
            onLimpar={() => setFilterPayments([])}
          />

          <FiltroMulti
            e2e="filtro-modulo"
            icone={Layers}
            rotulo="Módulo"
            opcoes={MODULOS_RESUMO.map((m) => ({ value: m.key, label: m.label }))
              .concat([{ value: FILTRO_CLEO, label: "CLEO (WhatsApp)" }, { value: "__integration__", label: "Integração" }])}
            selecionados={filterModules.includes(INTEGRATION_MODULES[0])
              ? [...filterModules.filter((k) => !INTEGRATION_MODULES.includes(k)), "__integration__"]
              : filterModules}
            onToggle={(valor, marcado) => {
              const chaves = valor === "__integration__" ? INTEGRATION_MODULES : [valor];
              setFilterModules((prev) => marcado
                ? [...prev.filter((k) => !chaves.includes(k)), ...chaves]
                : prev.filter((k) => !chaves.includes(k)));
            }}
            onLimpar={() => setFilterModules([])}
          />

          <FiltroMulti
            e2e="filtro-caixa"
            icone={DollarSign}
            rotulo="Caixa"
            unico
            opcoes={[{ value: "todos", label: "Todos os caixas" }].concat(
              (caixasList || []).map((c) => ({
                value: String(c.id), label: `Caixa #${c.id} — ${c.usuario_nome || "?"}`,
              })))}
            selecionados={filterCaixa !== "todos" ? [filterCaixa] : []}
            onToggle={(valor) => {
              setFilterCaixa(valor);
              if (valor === "todos" && filterCaixaId) {
                searchParams.delete("caixa_id"); setSearchParams(searchParams);
              }
            }}
            onLimpar={() => setFilterCaixa("todos")}
          />

          {entregadoresList.length > 0 && (
            <FiltroMulti
              e2e="filtro-entregador"
              icone={Bike}
              rotulo="Entregador"
              opcoes={entregadoresList.map((n) => ({ value: n, label: n }))}
              selecionados={filterEntregadores}
              onToggle={(valor, marcado) => setFilterEntregadores((prev) =>
                marcado ? [...prev, valor] : prev.filter((v) => v !== valor))}
              onLimpar={() => setFilterEntregadores([])}
            />
          )}

          <FiltroMulti
            e2e="filtro-origem"
            icone={Megaphone}
            rotulo="Origem"
            opcoes={OPCOES_ORIGEM}
            selecionados={filterOrigens}
            onToggle={(valor, marcado) => setFilterOrigens((prev) =>
              marcado ? [...prev, valor] : prev.filter((v) => v !== valor))}
            onLimpar={() => setFilterOrigens([])}
          />

          <div className="flex items-center gap-2 ml-auto">
            {/* Só ícone: com seis caixas de seleção na mesma linha, os rótulos
                aqui empurravam as ações pra uma segunda linha. */}
            <Button variant="outline" size="icon" onClick={handleExportExcel} disabled={filteredPedidos.length === 0} className="h-9 w-9" title="Exportar para Excel">
              <Download className="w-4 h-4" />
            </Button>
            <div className="flex rounded-lg border overflow-hidden h-9">
              <button onClick={() => setViewMode("grade")} title="Ver em grade" className={`px-2.5 flex items-center transition-colors ${viewMode === "grade" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}>
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button onClick={() => setViewMode("lista")} title="Ver em lista" className={`px-2.5 flex items-center transition-colors border-l ${viewMode === "lista" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}>
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Rodapé do card: o que está filtrado e quantos pedidos a página mostra. */}
        <div className="flex items-center gap-3 px-3 py-2 border-t bg-muted/30">
            <ChipsAtivos chips={chipsFiltros} onLimparTudo={handleClearFilters} />
          <div className="ml-auto shrink-0 text-xs text-muted-foreground">
            {loading ? <Skeleton className="h-3.5 w-20" /> : (
              <><span className="font-bold text-foreground">{fmtNum(paginatedPedidos.length)}</span> de {fmtNum(effectiveTotal)}</>
            )}
          </div>
        </div>
      </div>

      {/* Card destacado — Agendados pendentes (só quando houver) */}
      {stats.agendado > 0 && (
        <div className="mb-4 rounded-xl border-2 border-indigo-300 dark:border-indigo-700 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-950/40 dark:to-blue-950/40 p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-indigo-500 text-white flex items-center justify-center flex-shrink-0">
            <CalendarIcon className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-indigo-700 dark:text-indigo-300">
              {stats.agendado} pedido{stats.agendado === 1 ? "" : "s"} agendado{stats.agendado === 1 ? "" : "s"} pendente{stats.agendado === 1 ? "" : "s"} de aprovação
            </p>
            <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-0.5">
              Clique em "Aprovar" no card do pedido pra enviar pra produção e disparar a impressão.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setFilterStatuses(["agendado"])}
            className="border-indigo-400 text-indigo-700 hover:bg-indigo-100 dark:text-indigo-300 dark:border-indigo-600 dark:hover:bg-indigo-900/40"
          >
            Ver agendados
          </Button>
        </div>
      )}

      {/* Abaixo de xl o painel lateral não cabe: o resumo vira esta faixa. */}
      <ResumoFaixa
        stats={resumo}
        carregando={carregandoResumo}
        grupos={grupos}
        automacao={automacao}
        filtrado={hasActiveFilters}
        onAbrirGrupo={alternarGrupo}
      />
      {/* Lista de Pedidos. Carregamento com cards (25/10/2026): na primeira
          abertura, a grade (ou a tabela) piscando no lugar de "Nenhum pedido
          encontrado". `loading` só é true até a 1ª resposta — filtro e refresh
          de 10 s não voltam ao esqueleto. */}
      {loading ? (
        viewMode === "grade" ? (
          <CarregandoGrade quantos={12} altura="h-[228px]"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3" />
        ) : (
          <CarregandoTabela linhas={10} colunas={8} />
        )
      ) : filteredPedidos.length === 0 ? (
        <div className="bg-card rounded-xl border p-12 text-center">
          <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
          <h3 className="text-lg font-medium mb-2">Nenhum pedido encontrado</h3>
          <p className="text-muted-foreground">
            {searchTerm || filterStatuses.length > 0
              ? "Tente ajustar os filtros de busca"
              : "Os pedidos realizados aparecerão aqui"}
          </p>
        </div>
      ) : viewMode === "grade" ? (
        /* Visualização em Grade */
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
          {paginatedPedidos.map(pedido => {
            const status = statusConfig[getNormalizedStatus(pedido.status)] || statusConfig.producao;
            const StatusIcon = status.icon;
            const modulo = getModulo(pedido.modulo);
            const ModuloIcon = modulo?.icon || Package;
            const isCancelado = getNormalizedStatus(pedido.status) === "cancelado";
            const partesPg = partesDoPedido(pedido);
            const isAgendado = getNormalizedStatus(pedido.status) === "agendado";
            const isMesa = pedido.modulo === 'Mesas' || pedido.mesa_id;

            return (
              <div
                key={pedido.id}
                onClick={() => handleOpenDetails(pedido)}
                className={`h-full flex flex-col rounded-lg border shadow-sm hover:shadow-md transition-all cursor-pointer overflow-hidden ${
                  isCancelado
                    ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800'
                    : isAgendado
                    ? 'bg-indigo-50/60 dark:bg-indigo-950/20 border-indigo-300 dark:border-indigo-700 ring-1 ring-indigo-300/40'
                    : 'bg-card'
                }`}
              >
                {/* Banner topo: agendamento (pendente) */}
                {isAgendado && pedido.scheduled_at && (
                  <div className="shrink-0 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white px-3 py-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider">
                    <CalendarIcon className="w-3 h-3 shrink-0" />
                    <span className="truncate">Entrega prevista {fmtScheduledAt(pedido.scheduled_at)}</span>
                  </div>
                )}
                {/* Header compacto: código + módulo + status */}
                {/* flex-wrap: no card estreito (4-5 colunas, resumo aberto) o selo do
                    módulo passava por baixo do status e virava "CanceTadom". Agora,
                    sem espaço, o status desce para a linha de baixo. */}
                <div className={`shrink-0 px-3 py-2 border-b flex flex-wrap items-center justify-between gap-x-2 gap-y-1 ${
                  isCancelado ? 'border-red-200 dark:border-red-800' : ''
                }`}>
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className={`font-mono font-bold text-sm shrink-0 ${isCancelado ? 'text-red-600 dark:text-red-400' : ''}`}>{pedido.codigo || "#-----"}</span>
                    {(pedido.modulo === 'Balcao' || pedido.modulo === 'Totem') && pedido.senha_balcao && (
                      <span className="bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap shrink-0" title={`Senha ${pedido.senha_balcao}`}>
                        Senha {pedido.senha_balcao}
                      </span>
                    )}
                    {modulo && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex items-center gap-0.5 min-w-0 ${modulo.color}`} title={modulo.label}>
                        <ModuloIcon className="w-2.5 h-2.5 shrink-0" />
                        <span className="truncate">{modulo.label}</span>
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {pedido.review_rating_avg != null && (
                      <span
                        className="px-1.5 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-0.5 bg-amber-400/15 text-amber-600 dark:text-amber-400"
                        title={`Avaliação: ${Number(pedido.review_rating_avg).toFixed(1)} / 5`}
                      >
                        <Star className="w-2.5 h-2.5 fill-current" strokeWidth={1.5} />
                        {Number(pedido.review_rating_avg).toFixed(1).replace('.', ',')}
                      </span>
                    )}
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-0.5 ${status.color}`}>
                      <StatusIcon className="w-2.5 h-2.5" />
                      {status.label}
                    </span>
                  </div>
                </div>

                {/* Conteúdo padronizado - altura fixa */}
                <div className="flex-1 px-3 py-2 flex flex-col min-h-[86px]">
                  {/* Linha 1: Cliente + Telefone + Horário (altura fixa: com ou sem telefone) */}
                  <div className="flex items-start justify-between gap-2 min-h-[34px]">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{pedido.cliente?.nome || "Cliente"}</p>
                      {pedido.cliente?.telefone && (
                        <a
                          href={`https://wa.me/${pedido.cliente.telefone.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-[10px] text-green-600 dark:text-green-400 hover:underline flex items-center gap-0.5"
                        >
                          <Phone className="w-2.5 h-2.5" />
                          {formatPhoneNumber(pedido.cliente.telefone)}
                        </a>
                      )}
                    </div>
                    <span className="text-[10px] text-muted-foreground shrink-0">
                      {pedido.created_at ? `${new Date(pedido.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })} ${new Date(pedido.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}` : ""}
                    </span>
                  </div>

                  {/* Linha 2: Tipo + Itens + Caixa */}
                  <div className="flex items-center gap-2 flex-wrap mt-1.5">
                    {isMesa ? (
                      <span className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                        <UtensilsCrossed className="w-2.5 h-2.5" /> {pedido.endereco?.endereco || 'Mesa'}
                      </span>
                    ) : (() => {
                      // Balcão/Totem ("No local") e Comandas apareciam como "Entrega".
                      const selo = seloTipoEntrega(pedido);
                      if (!selo) return null;
                      const SeloIcon = selo.Icon;
                      return (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5 ${selo.cor}`}>
                          <SeloIcon className="w-2.5 h-2.5" /> {selo.label}
                        </span>
                      );
                    })()}
                    <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                      <Package className="w-2.5 h-2.5" /> {pedido.items?.length || 0} item(s)
                    </span>
                    {pedido.caixa_id && (
                      <span className="text-[10px] text-orange-600 dark:text-orange-400">Cx#{pedido.caixa_id}</span>
                    )}
                    <SeloReaberto pedido={pedido} />
                    <SeloCleo pedido={pedido} />
                    {!isAgendado && pedido.scheduled_at && (
                      <span className="text-[10px] bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 px-1.5 py-0.5 rounded-full flex items-center gap-0.5 max-w-[150px]"
                        title={`Foi agendado para ${fmtScheduledAt(pedido.scheduled_at)}`}>
                        <CalendarIcon className="w-2.5 h-2.5 shrink-0" /> <span className="truncate">Agendado</span>
                      </span>
                    )}
                    <SeloOrigem origem={pedido.origem_campanha} compacto />
                  </div>

                  {/* Linha 3: Info extra (endereço ou permanência) */}
                  <div className="mt-auto pt-1">
                    {isMesa ? (
                      <p className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                        <Clock className="w-2.5 h-2.5" /> Permanência: {formatPermanencia(pedido.mesa_opened_at || pedido.created_at, pedido.mesa_closed_at || pedido.ts_concluido)}
                      </p>
                    ) : pedido.tipoEntrega === 'delivery' && pedido.endereco ? (
                      <p className="text-[10px] text-muted-foreground truncate">
                        <MapPin className="w-2.5 h-2.5 inline mr-0.5" />
                        {pedido.endereco.endereco}{pedido.endereco.numero && `, ${pedido.endereco.numero}`}{pedido.endereco.bairro && ` - ${pedido.endereco.bairro}`}
                      </p>
                    ) : (
                      <p className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                        <CalendarIcon className="w-2.5 h-2.5" /> {pedido.created_at ? new Date(pedido.created_at).toLocaleDateString("pt-BR") : ""}
                      </p>
                    )}
                  </div>
                </div>

                {/* Rodapé com a MESMA altura em todo card (dono, 27/09/2026): sempre
                    duas linhas fixas — formas de pagamento em cima (uma linha; o
                    texto inteiro no title) e selo NF, impressora e total embaixo.
                    Antes, card com uma forma tinha uma linha só e com duas ou mais
                    ganhava outra, e a grade ficava com rodapés desencontrados. */}
                <div className={`shrink-0 px-3 py-1.5 border-t flex flex-col gap-1 ${
                  isCancelado
                    ? 'bg-red-100/50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                    : isAgendado
                    ? 'bg-indigo-100/40 dark:bg-indigo-950/40 border-indigo-200 dark:border-indigo-800'
                    : 'bg-muted/30'
                }`}>
                  <div className={`flex items-center gap-1 text-xs min-w-0 h-4 overflow-hidden ${isCancelado ? 'text-red-500' : 'text-muted-foreground'}`}
                    title={textoPartes(partesPg)} data-e2e="card-rodape-formas">
                    <FormasPagamentoPedido partes={partesPg} variante="card" emojiDe={emojiDe} />
                  </div>
                  <div className="flex items-center justify-end gap-1.5 h-7" data-e2e="card-rodape-total">
                    {isAgendado && (
                      <Button
                        onClick={(e) => handleAprovarAgendado(pedido, e)}
                        title="Aprovar: envia pra produção e imprime"
                        className="h-7 px-2.5 bg-green-600 hover:bg-green-700 text-white text-[11px] font-bold gap-1"
                      >
                        <CheckCircle className="w-3.5 h-3.5" />
                        Aprovar
                      </Button>
                    )}
                    <SeloNotaFiscal pedido={pedido} compacto />
                    {(getNormalizedStatus(pedido.status) === "concluido" || getNormalizedStatus(pedido.status) === "cancelado") && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleReimprimirCupom(pedido); }}
                        className="p-1 rounded hover:bg-muted transition-colors"
                        title="Reimprimir cupom"
                      >
                        <Printer className="w-3.5 h-3.5 text-muted-foreground" />
                      </button>
                    )}
                    <span className={`font-bold text-sm ${isCancelado ? 'text-red-600 dark:text-red-400 line-through' : 'text-primary'}`}>
                      R$ {(pedido.total || 0).toFixed(2).replace(".", ",")}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Paginação para Grade */}
        {filteredPedidos.length > 0 && (
          <div className="bg-card rounded-xl border shadow-sm overflow-hidden mt-4">
            <TablePagination
              currentPage={currentPage}
              totalItems={effectiveTotal}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              opcoesPorPagina={[25, 50, 75, 100]}
              onItemsPerPageChange={(value) => {
                setItemsPerPage(value);
                setCurrentPage(1);
              }}
            />
          </div>
        )}
        </>
      ) : (
        /* Visualização em Lista */
        <>
        <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Código</TableHead>
                <TableHead className="w-24">Módulo</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead className="w-32">Data/Hora</TableHead>
                <TableHead className="w-20 text-center">Itens</TableHead>
                <TableHead className="w-28">Pagamento</TableHead>
                <TableHead className="w-28">Status</TableHead>
                <TableHead className="w-24 text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedPedidos.map(pedido => {
                const status = statusConfig[getNormalizedStatus(pedido.status)] || statusConfig.producao;
                const StatusIcon = status.icon;
                const modulo = getModulo(pedido.modulo);
                const ModuloIcon = modulo?.icon || Package;
                const isCancelado = getNormalizedStatus(pedido.status) === "cancelado";

                return (
                  <TableRow
                    key={pedido.id}
                    onClick={() => handleOpenDetails(pedido)}
                    className={`cursor-pointer ${
                      isCancelado
                        ? 'bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-900/40'
                        : 'hover:bg-muted/50'
                    }`}
                  >
                    <TableCell className={`font-mono font-bold ${isCancelado ? 'text-red-600 dark:text-red-400' : ''}`}>
                      {pedido.codigo || "#-----"}
                      <SeloReaberto pedido={pedido} className="ml-1 align-middle" />
                      <SeloCleo pedido={pedido} className="ml-1 align-middle" />
                      {(pedido.modulo === 'Balcao' || pedido.modulo === 'Totem') && pedido.senha_balcao && (
                        <span className="ml-1 bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 px-1 py-0.5 rounded text-[9px] font-bold">
                          S{pedido.senha_balcao}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {modulo ? (
                        <span className={`px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1 w-fit ${modulo.color}`}>
                          <ModuloIcon className="w-3 h-3" />
                          {modulo.label}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                          {pedido.cliente?.foto ? (
                            <img src={pedido.cliente.foto} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <User className="w-4 h-4 text-muted-foreground" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{pedido.cliente?.nome}</p>
                          {pedido.cliente?.telefone && (
                            <a
                              href={`https://wa.me/${pedido.cliente.telefone.replace(/\D/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-xs text-green-600 dark:text-green-400 hover:underline flex items-center gap-0.5"
                            >
                              <Phone className="w-3 h-3" />
                              {formatPhoneNumber(pedido.cliente.telefone)}
                            </a>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{formatDateTime(pedido.created_at)}</TableCell>
                    <TableCell className="text-center">{pedido.items?.length || 0}</TableCell>
                    <TableCell>
                      <FormasPagamentoPedido partes={partesDoPedido(pedido)} variante="tabela" emojiDe={emojiDe} />
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit ${status.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {status.label}
                      </span>
                    </TableCell>
                    <TableCell className={`text-right font-bold ${isCancelado ? 'text-red-600 dark:text-red-400 line-through' : ''}`}>
                      R$ {(pedido.total || 0).toFixed(2).replace(".", ",")}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          
          {/* Paginação para Lista */}
          {filteredPedidos.length > 0 && (
            <TablePagination
              currentPage={currentPage}
              totalItems={effectiveTotal}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              opcoesPorPagina={[25, 50, 75, 100]}
              onItemsPerPageChange={(value) => {
                setItemsPerPage(value);
                setCurrentPage(1);
              }}
            />
          )}
        </div>
        </>
      )}

        </div>
        {resumoAberto && (
          <div
            className="hidden xl:block shrink-0"
            style={{ width: LARGURA_PAINEL }}
          >
            <PainelResumo
              stats={resumo}
              grupos={grupos}
              automacao={automacao}
              carregando={carregandoResumo}
              grupoAberto={grupoAberto}
              variacaoAtiva={variacaoAtiva}
              onAlternarGrupo={alternarGrupo}
              onFiltrarVariacao={filtrarPorVariacao}
            />
          </div>
        )}
      </div>

      {/* Dialog Detalhes do Pedido — estilo Delivery popup */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-[100vw] sm:max-w-[95vw] md:max-w-lg w-full h-[100dvh] sm:h-auto sm:max-h-[90vh] flex flex-col m-0 sm:m-4 rounded-none sm:rounded-lg p-0" aria-describedby={undefined}>
          <DialogTitle className="sr-only">Detalhes do Pedido</DialogTitle>
          {selectedPedido && (
            <>
              {/* Header fixo */}
              <div className="flex items-center gap-2 px-4 py-3 border-b shrink-0">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-lg font-bold">{selectedPedido.codigo || "#-----"}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${statusConfig[getNormalizedStatus(selectedPedido.status)]?.color}`}>
                      {statusConfig[getNormalizedStatus(selectedPedido.status)]?.label}
                    </span>
                    <SeloReaberto pedido={selectedPedido} />
                    <SeloCleo pedido={selectedPedido} />
                    {(() => { const mod = getModulo(selectedPedido.modulo); if (!mod) return null; const Icon = mod.icon; return (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex items-center gap-0.5 ${mod.color}`}>
                        <Icon className="w-2.5 h-2.5" />
                        {mod.label}
                      </span>
                    ); })()}
                    {selectedPedido.caixa_id && (
                      <span className="text-[10px] text-orange-600 dark:text-orange-400 font-medium">Cx#{selectedPedido.caixa_id}</span>
                    )}
                    <SeloOrigem origem={selectedPedido.origem_campanha} />
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-xs text-muted-foreground">
                      {formatDateTime(selectedPedido.created_at)}
                    </p>
                    <SeloNotaFiscal pedido={selectedPedido} />
                  </div>
                </div>
                {/* Print dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-2 rounded-md hover:bg-muted transition-colors mr-6" title="Imprimir 2ª via">
                      <Printer className="w-5 h-5 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {(selectedPedido.modulo === 'Mesas' || selectedPedido.mesa_id) ? (
                      <DropdownMenuItem onClick={() => handleReimprimirCupom(selectedPedido)}>
                        <Receipt className="w-4 h-4 mr-2" /> Cupom Fechamento
                      </DropdownMenuItem>
                    ) : (
                      <>
                        <DropdownMenuItem onClick={() => handleReimprimirCupom(selectedPedido, 'ambos')}>
                          <Printer className="w-4 h-4 mr-2" /> Ambos
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleReimprimirCupom(selectedPedido, 'entrega')}>
                          <FileText className="w-4 h-4 mr-2" /> Entrega
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleReimprimirCupom(selectedPedido, 'preparo')}>
                          <ChefHat className="w-4 h-4 mr-2" /> Preparo
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Conteúdo scrollável */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {/* Cliente + Endereço */}
                <div className="bg-muted/30 rounded-lg p-2.5 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm">{selectedPedido.cliente?.nome || "Cliente"}</p>
                      {selectedPedido.cliente?.telefone && (
                        <a
                          href={`https://wa.me/${selectedPedido.cliente.telefone.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-green-600 hover:underline flex items-center gap-1 mt-0.5"
                        >
                          <Phone className="w-3 h-3" /> {formatPhoneNumber(selectedPedido.cliente.telefone)}
                        </a>
                      )}
                    </div>
                    {selectedPedido.entregador_nome && (
                      <div className="text-right shrink-0">
                        <p className="text-[10px] text-muted-foreground">Entregador</p>
                        <p className="text-xs font-medium flex items-center gap-1 justify-end">
                          <Bike className="w-3 h-3" /> {selectedPedido.entregador_nome}
                        </p>
                      </div>
                    )}
                  </div>
                  {/* Endereço de entrega */}
                  {selectedPedido.tipoEntrega === 'delivery' && selectedPedido.endereco && (
                    <div className="flex items-start gap-1.5 pt-1.5 border-t border-muted">
                      <MapPin className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />
                      <div className="text-xs">
                        <p className="font-medium">
                          {selectedPedido.endereco.endereco}{selectedPedido.endereco.numero && `, ${selectedPedido.endereco.numero}`}
                        </p>
                        {selectedPedido.endereco.bairro && <p className="text-muted-foreground">{selectedPedido.endereco.bairro}</p>}
                        {selectedPedido.endereco.complemento && <p className="text-muted-foreground italic">{selectedPedido.endereco.complemento}</p>}
                        {selectedPedido.endereco.cep && <p className="text-muted-foreground">CEP: {selectedPedido.endereco.cep}</p>}
                      </div>
                    </div>
                  )}
                  {/* Tipo entrega badge */}
                  {!(selectedPedido.modulo === 'Mesas' || selectedPedido.mesa_id) && (
                    <div className="flex items-center gap-2 pt-1">
                      {(() => {
                        const selo = seloTipoEntrega(selectedPedido);
                        if (!selo) return null;
                        const SeloIcon = selo.Icon;
                        return (
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-0.5 ${selo.cor}`}>
                            <SeloIcon className="w-3 h-3" /> {selo.label}
                          </span>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Mesa info */}
                {(selectedPedido.modulo === 'Mesas' || selectedPedido.mesa_id) && (
                  <div className="bg-muted/30 rounded-lg p-2.5">
                    <p className="text-xs font-semibold mb-1.5 flex items-center gap-1.5 text-muted-foreground">
                      <UtensilsCrossed className="w-3.5 h-3.5" /> Mesa
                    </p>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Abertura:</span>
                        <span className="font-medium">{formatDateTime(selectedPedido.mesa_opened_at)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Fechamento:</span>
                        <span className="font-medium">{formatDateTime(selectedPedido.mesa_closed_at || selectedPedido.ts_concluido)}</span>
                      </div>
                      <div className="flex justify-between border-t border-muted pt-1 mt-1">
                        <span className="text-muted-foreground font-medium">Permanência:</span>
                        <span className="font-bold">{formatPermanencia(selectedPedido.mesa_opened_at || selectedPedido.created_at, selectedPedido.mesa_closed_at || selectedPedido.ts_concluido)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Senha do Balcão */}
                {(selectedPedido.modulo === 'Balcao' || selectedPedido.modulo === 'Totem') && selectedPedido.senha_balcao && (
                  <div className="bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Senha do Balcão</p>
                    <p className="text-4xl font-black text-orange-500">{selectedPedido.senha_balcao}</p>
                  </div>
                )}

                {/* Tempos compactos (calculados dos timestamps, igual Delivery) */}
                {(() => {
                  const p = selectedPedido;
                  const isMesa = p.modulo === 'Mesas' || p.mesa_id;
                  if (isMesa) return null;
                  const isPickup = p.tipoEntrega === 'pickup';
                  const steps = isPickup
                    ? [
                        { label: 'Recebido', ts: p.created_at },
                        { label: 'Produção', ts: p.ts_producao },
                        { label: 'Pronto', ts: p.ts_pronto },
                        { label: 'Retirado', ts: p.ts_concluido },
                      ]
                    : [
                        { label: 'Recebido', ts: p.created_at },
                        { label: 'Produção', ts: p.ts_producao },
                        { label: 'Pronto', ts: p.ts_pronto },
                        // 'Na Bag' descontinuado — só exibido para pedidos legados que gravaram ts_na_bag
                        ...(p.ts_na_bag ? [{ label: 'Na Bag', ts: p.ts_na_bag }] : []),
                        { label: 'Em Rota', ts: p.ts_em_rota },
                        { label: 'Entregue', ts: p.ts_concluido },
                      ];
                  const fmtElapsed = (ms) => {
                    if (!ms || ms < 0) return '';
                    const totalSec = Math.floor(ms / 1000);
                    if (totalSec < 60) return `${totalSec}s`;
                    const min = Math.floor(totalSec / 60);
                    const sec = totalSec % 60;
                    if (min < 60) return sec > 0 ? `${min}min ${sec}s` : `${min}min`;
                    const h = Math.floor(min / 60);
                    const m = min % 60;
                    return m > 0 ? `${h}h ${m}min` : `${h}h`;
                  };
                  let totalMs = 0;
                  const stepsWithElapsed = steps.map((s, i) => {
                    let diffMs = 0;
                    if (s.ts) {
                      const nextTs = (i < steps.length - 1 && steps[i + 1]?.ts) ? new Date(steps[i + 1].ts).getTime() : null;
                      if (nextTs) {
                        diffMs = nextTs - new Date(s.ts).getTime();
                      }
                      if (diffMs > 0) totalMs += diffMs;
                    }
                    return { ...s, diffMs };
                  });
                  const hasAnyTs = steps.some(s => s.ts);
                  if (!hasAnyTs) return null;
                  return (
                    <div className="bg-muted/30 rounded-lg p-2.5">
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px]">
                        {stepsWithElapsed.map((s, i) => (
                          <span key={i} className={`flex items-center gap-0.5 ${s.ts ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                            {s.ts ? <Check className="w-3 h-3 text-primary" /> : <span className="w-3 h-3 rounded-full border border-muted-foreground/30 inline-block" />}
                            {s.label}
                            {s.diffMs > 0 && <span className="font-normal ml-0.5 text-orange-500">{fmtElapsed(s.diffMs)}</span>}
                          </span>
                        ))}
                      </div>
                      {totalMs > 0 && (
                        <div className="flex items-center gap-1 mt-1.5 pt-1.5 border-t border-muted text-[10px] text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          <span>Tempo total: <span className="font-semibold text-foreground">{fmtElapsed(totalMs)}</span></span>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Avaliação do cliente (se o pedido foi avaliado) */}
                {selectedPedido.review_rating_avg != null && (
                  <div className="rounded-lg border border-amber-400/30 bg-amber-50 dark:bg-amber-500/5 p-3">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <p className="text-xs font-semibold flex items-center gap-1.5 text-amber-700 dark:text-amber-300">
                        <Sparkles className="w-3.5 h-3.5" /> Avaliação do cliente
                      </p>
                      <div className="inline-flex items-center gap-1 text-amber-500">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star
                            key={n}
                            className={`w-4 h-4 ${n <= Math.round(Number(selectedPedido.review_rating_avg) || 0) ? 'fill-current' : 'opacity-30'}`}
                            strokeWidth={1.5}
                          />
                        ))}
                        <span className="text-sm font-extrabold ml-1">
                          {Number(selectedPedido.review_rating_avg).toFixed(1).replace('.', ',')}
                        </span>
                        <span className="text-[10px] opacity-70">/ 5</span>
                      </div>
                    </div>
                    {selectedPedido.review_ratings && Object.keys(selectedPedido.review_ratings).length > 1 && (
                      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1">
                        {Object.entries(selectedPedido.review_ratings).map(([k, v]) => (
                          <div key={k} className="flex items-center justify-between text-[11px]">
                            <span className="capitalize text-muted-foreground truncate">{k}</span>
                            <span className="inline-flex items-center gap-0.5 shrink-0">
                              {[1, 2, 3, 4, 5].map((n) => (
                                <Star
                                  key={n}
                                  className={`w-2.5 h-2.5 text-amber-500 ${n <= Number(v) ? 'fill-current' : 'opacity-30'}`}
                                  strokeWidth={1.5}
                                />
                              ))}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {selectedPedido.review_comment && (
                      <p className="mt-2 text-xs italic text-foreground/80 leading-snug">
                        "{selectedPedido.review_comment}"
                      </p>
                    )}
                  </div>
                )}

                {/* Itens */}
                <div>
                  <p className="text-xs font-semibold mb-1.5 flex items-center gap-1.5 text-muted-foreground">
                    <Package className="w-3.5 h-3.5" /> Itens ({selectedPedido.items?.length || 0})
                  </p>
                  <div className="divide-y divide-muted">
                    {selectedPedido.items?.map((item, index) => {
                      const photoUrl = item.photo_url || item.foto_url
                        || findProductPhoto(item.name || item.nome, productsMap)
                        || (item.id && productsMap[`id_${item.id}`]);
                      const photoSrc = photoUrl ? getImageUrl(photoUrl) : null;
                      return (
                        <div key={index} className="py-2 first:pt-0">
                          <div className="flex gap-2 items-start">
                            <div className="w-10 h-10 rounded bg-muted flex-shrink-0 overflow-hidden relative flex items-center justify-center">
                              <Package className="w-4 h-4 text-muted-foreground" />
                              {photoSrc && (
                                <img src={photoSrc} alt="" className="w-full h-full object-cover absolute inset-0" onError={(e) => { e.target.style.display = "none"; }} />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-start">
                                <p className="text-sm font-medium">
                                  {qtdDoItem(item.quantity || item.quantidade || 1, medidaDoItem(item, produtosCadastro))} {item.name || item.nome}
                                  {item.combo_type && <span className="text-xs text-orange-500 ml-1">({item.combo_type === 'combo' ? 'Combo' : 'Simples'})</span>}
                                  {item.oferta_id && <span className="ml-1.5 align-middle"><SeloOfertaItem item={item} /></span>}
                                </p>
                                <div className="shrink-0 ml-2 text-right">
                                  {item.oferta_preco_cheio && (
                                    <p className="text-[11px] text-muted-foreground line-through leading-none">
                                      R$ {(Number(item.oferta_preco_cheio) * Number(item.quantity || item.quantidade || 1)).toFixed(2).replace(".", ",")}
                                    </p>
                                  )}
                                  <p className="text-sm font-medium">
                                    R$ {((Number(item.final_price ?? item.preco_unitario ?? item.unit_price ?? item.sale_price ?? item.preco ?? 0)) * Number(item.quantity || item.quantidade || 1)).toFixed(2).replace(".", ",")}
                                  </p>
                                </div>
                              </div>
                              {item.subitems && item.subitems.length > 0 && (
                                <div className="mt-1 space-y-1">
                                  {item.subitems.map((sub, subIdx) => {
                                    const subName = sub.nome || sub.name || '';
                                    const subPhotoUrl = sub.photo_url || sub.foto_url || findProductPhoto(subName, productsMap);
                                    const subPhotoSrc = subPhotoUrl ? getImageUrl(subPhotoUrl) : null;
                                    return (
                                      <div key={subIdx} className="flex items-center gap-1.5">
                                        <div className="w-6 h-6 rounded bg-muted flex-shrink-0 overflow-hidden relative flex items-center justify-center">
                                          <Package className="w-2.5 h-2.5 text-muted-foreground" />
                                          {subPhotoSrc && (
                                            <img src={subPhotoSrc} alt="" className="w-full h-full object-cover absolute inset-0" onError={(e) => { e.target.style.display = "none"; }} />
                                          )}
                                        </div>
                                        <p className="text-[10px] text-muted-foreground leading-relaxed">
                                          <span className="text-orange-400">+ </span>
                                          {sub.step_name && <span className="opacity-70">{sub.step_name}: </span>}
                                          {subName}
                                          {(sub.preco > 0) && <span className="text-orange-500"> (+R$ {sub.preco.toFixed(2).replace(".", ",")})</span>}
                                        </p>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {(!item.subitems || item.subitems.length === 0) && item.etapas && item.etapas.length > 0 && (
                                <div className="mt-1 space-y-1">
                                  {item.etapas.map((etapa, etapaIdx) => (
                                    <div key={etapaIdx}>
                                      {etapa.itens?.map((etapaItem, eiIdx) => {
                                        const eiPhoto = findProductPhoto(etapaItem, productsMap);
                                        const eiSrc = eiPhoto ? getImageUrl(eiPhoto) : null;
                                        return (
                                          <div key={eiIdx} className="flex items-center gap-1.5">
                                            <div className="w-6 h-6 rounded bg-muted flex-shrink-0 overflow-hidden relative flex items-center justify-center">
                                              <Package className="w-2.5 h-2.5 text-muted-foreground" />
                                              {eiSrc && (
                                                <img src={eiSrc} alt="" className="w-full h-full object-cover absolute inset-0" onError={(e) => { e.target.style.display = "none"; }} />
                                              )}
                                            </div>
                                            <p className="text-[10px] text-muted-foreground leading-relaxed">
                                              <span className="text-orange-400">+ </span>
                                              <span className="opacity-70">{etapa.etapa}: </span>
                                              {etapaItem}
                                            </p>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ))}
                                </div>
                              )}
                              {(item.observation || item.observacao) && (
                                <p className="text-[10px] text-yellow-700 dark:text-yellow-400 mt-0.5 italic">* {item.observation || item.observacao}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Observação do pedido */}
                {(selectedPedido.observacao || selectedPedido.observacoes) && (
                  <div className="bg-yellow-50 dark:bg-yellow-950/20 rounded-lg p-2.5 border border-yellow-200 dark:border-yellow-800">
                    <p className="text-xs font-semibold text-yellow-700 dark:text-yellow-400 flex items-center gap-1 mb-0.5">
                      <MessageSquare className="w-3.5 h-3.5" /> Observação
                    </p>
                    <p className="text-sm">{selectedPedido.observacao || selectedPedido.observacoes}</p>
                  </div>
                )}
              </div>

              {/* Footer fixo */}
              <div className="border-t px-4 py-3 shrink-0 space-y-2">
                {/* Breakdown completo: Subtotal → Frete → Desconto/Cupom → Total
                    Sempre exibido quando há diferença entre soma de itens e total,
                    pra justificar visualmente cada R\$ que sai/entra do valor final. */}
                {(() => {
                  // Subtotal usa final_price quando existe (preço REAL pago, já considerando
                  // combo/simples e adicionais). Cai pro sale_price/preco como fallback legado.
                  // Campanha Inteligente: item com oferta entra no subtotal pelo preço CHEIO e o
                  // desconto aparece em linha própria — de onde veio o preço fica explícito.
                  const itemsSubtotal = (selectedPedido.items || []).reduce(
                    (s, it) => s + ((it.oferta_preco_cheio ? precoCheioOuPago(it) : Number(it.final_price ?? it.preco_unitario ?? it.unit_price ?? it.sale_price ?? it.preco ?? 0)) * Number(it.quantity || it.quantidade || 1)),
                    0
                  );
                  const descExclusivo = descontoExclusivo(selectedPedido.items);
                  const cupomDesc = Number(selectedPedido.cupom_desconto || 0);
                  const manualDesc = Number(selectedPedido.desconto_manual || 0);
                  const totalDesc = cupomDesc + manualDesc;
                  const taxaEntrega = Number(selectedPedido.valor_entrega || selectedPedido.taxaEntrega || 0);
                  const totalFinal = Number(selectedPedido.total || 0);
                  // Diferença residual (se total ≠ subtotal+frete-desc, mostra como ajuste)
                  const expected = itemsSubtotal + taxaEntrega - totalDesc - descExclusivo;
                  const ajusteResidual = totalFinal - expected;
                  // Só mostra o breakdown quando há ALGO além de itens (frete/desconto/ajuste)
                  const hasAnyAdjustment = taxaEntrega > 0 || totalDesc > 0 || descExclusivo > 0 || Math.abs(ajusteResidual) >= 0.01;
                  if (!hasAnyAdjustment) return null;

                  const cupomCod = selectedPedido.cupom_codigo;
                  const tipo = selectedPedido.desconto_tipo || (cupomDesc > 0 ? 'coupon' : 'fixed');
                  const pct = (selectedPedido.desconto_percentual && Number(selectedPedido.desconto_percentual) > 0)
                    ? Number(selectedPedido.desconto_percentual)
                    : (tipo === 'percent' && itemsSubtotal > 0 ? (manualDesc / itemsSubtotal * 100) : null);
                  // Para CUPOM: calcula o % representado pelo desconto sobre o subtotal — útil mesmo se
                  // o cupom for de valor fixo (mostra a 'mordida' que ele deu no pedido)
                  const cupomPct = (cupomCod && itemsSubtotal > 0) ? (cupomDesc / itemsSubtotal * 100) : null;
                  const fmtPct = (v) => v.toFixed(v % 1 === 0 ? 0 : 1).replace('.', ',');
                  let descLabel;
                  if (cupomCod) {
                    // Mostra: Cupom XPTO · 10% (quando há subtotal pra calcular)
                    descLabel = cupomPct != null
                      ? <>Cupom <span className="font-mono font-bold">{cupomCod}</span> <span className="opacity-80">· {fmtPct(cupomPct)}%</span></>
                      : <>Cupom <span className="font-mono font-bold">{cupomCod}</span></>;
                  } else if (tipo === 'percent' && pct != null) {
                    descLabel = `Desconto manual (${fmtPct(pct)}%)`;
                  } else {
                    descLabel = 'Desconto manual';
                  }

                  return (
                    <div className="rounded-md border bg-muted/30 px-2.5 py-2 text-xs space-y-1">
                      <div className="flex items-center justify-between text-muted-foreground">
                        <span>Subtotal ({(selectedPedido.items || []).length} {(selectedPedido.items || []).length === 1 ? 'item' : 'itens'})</span>
                        <span className="font-mono tabular-nums">R$ {itemsSubtotal.toFixed(2).replace('.', ',')}</span>
                      </div>
                      {taxaEntrega > 0 && (
                        <div className="flex items-center justify-between text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Truck className="w-3 h-3" /> Taxa de entrega
                          </span>
                          <span className="font-mono tabular-nums">+ R$ {taxaEntrega.toFixed(2).replace('.', ',')}</span>
                        </div>
                      )}
                      {descExclusivo > 0 && (
                        <div className="flex items-center justify-between text-primary" data-e2e="linha-desconto-exclusivo">
                          <span className="flex items-center gap-1.5 font-semibold">
                            <Sparkles className="w-3 h-3" /> Desconto exclusivo (Campanha Inteligente)
                          </span>
                          <span className="font-mono tabular-nums font-bold">− R$ {descExclusivo.toFixed(2).replace('.', ',')}</span>
                        </div>
                      )}
                      {totalDesc > 0 && (
                        <div className="flex items-center justify-between text-green-700 dark:text-green-400">
                          <span className="flex items-center gap-1.5 font-semibold min-w-0">
                            <Ticket className="w-3 h-3 shrink-0" />
                            <span className="truncate">{descLabel}</span>
                          </span>
                          <span className="font-mono tabular-nums font-bold shrink-0">− R$ {totalDesc.toFixed(2).replace('.', ',')}</span>
                        </div>
                      )}
                      {Math.abs(ajusteResidual) >= 0.01 && (() => {
                        // Quando o pedido é delivery e não tem valor_entrega/taxaEntrega explícito,
                        // o residual positivo é quase sempre o frete (ex: bairro com taxa).
                        // Mostra como "Frete" com ícone de Truck nesse caso.
                        const isDelivery = selectedPedido.tipoEntrega === 'delivery' || !!selectedPedido.endereco;
                        const isFreteResidual = isDelivery && ajusteResidual > 0 && taxaEntrega === 0;
                        return (
                          <div className={`flex items-center justify-between ${ajusteResidual < 0 ? 'text-green-700 dark:text-green-400' : (isFreteResidual ? 'text-muted-foreground' : 'text-amber-700 dark:text-amber-400')}`}>
                            <span className="flex items-center gap-1.5 font-medium">
                              {isFreteResidual ? <Truck className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
                              {isFreteResidual ? 'Frete' : 'Ajuste'}
                            </span>
                            <span className="font-mono tabular-nums">{ajusteResidual < 0 ? '−' : '+'} R$ {Math.abs(ajusteResidual).toFixed(2).replace('.', ',')}</span>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })()}
                <HistoricoReabertura historico={reabertura?.historico} />
                {/* Total + Pagamento */}
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-0.5">
                    <FormasPagamentoPedido partes={partesDoPedido(selectedPedido)} variante="detalhe" emojiDe={emojiDe} e2e="detalhe-pagamento" />
                    <DetalhePagamentoExtra pedido={selectedPedido} nomePagamento={nomePagamento} />
                  </div>
                  <span className={`font-bold text-lg ${getNormalizedStatus(selectedPedido.status) === 'cancelado' ? 'text-red-600 dark:text-red-400 line-through' : 'text-primary'}`}>
                    R$ {(selectedPedido.total || 0).toFixed(2).replace(".", ",")}
                  </span>
                </div>
                {/* Botões de ação */}
                {getNormalizedStatus(selectedPedido.status) !== "cancelado" && (
                  <div className="flex gap-2 items-start">
                    <ReabrirPedido pedido={selectedPedido} info={reabertura}
                      onReaberto={() => { setDetailsOpen(false); fetchPedidos(); }} />
                    <Button
                      variant="destructive"
                      size="sm"
                      className="flex-1 h-9 text-xs"
                      onClick={() => setCancelDialogOpen(true)}
                    >
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Cancelar Pedido
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmar Cancelamento com Senha Admin */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={(open) => {
        setCancelDialogOpen(open);
        if (!open) {
          setAdminPassword("");
          setCancelError("");
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar Pedido?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja cancelar o pedido {selectedPedido?.codigo}? 
              O pedido não será excluído, apenas terá seu status alterado para "Cancelado".
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4 space-y-4">
            {isIfoodSelected && selectedPedido?.id && (
              <CancelReasonSelect pedidoId={selectedPedido.id} value={ifoodCancelReason} onChange={setIfoodCancelReason} />
            )}
            <Label htmlFor="admin-password">{cancelaComASenhaDele ? "Sua senha" : "Senha do Administrador"}</Label>
            <Input
              id="admin-password"
              type="password"
              // Confirmação, não login: o navegador não deve preencher nem
              // sair procurando um campo de usuário pela tela pra completar.
              autoComplete="new-password"
              placeholder={cancelaComASenhaDele ? "Digite a sua senha para confirmar" : "Digite a senha do admin para confirmar"}
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              className="mt-2"
            />
            {cancelError && (
              <p className="text-sm text-red-500 mt-2">{cancelError}</p>
            )}
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleCancelPedido}
              disabled={!adminPassword || (isIfoodSelected && !ifoodCancelReason?.code)}
            >
              Cancelar Pedido
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Wrapper que carrega o estado das notas uma vez pra toda a tela — os selos
// dos cards leem daí em vez de cada um consultar a API.
export default function Pedidos(props) {
  return (
    <NotasFiscaisProvider>
      <PedidosInterno {...props} />
    </NotasFiscaisProvider>
  );
}
